import { ColCount } from '../ui/LayoutMenu';
import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import StandardPageHeader from './StandardPageHeader';
import { OnboardingTour } from '../ui/OnboardingChecklist';
import { currentRuntime } from '../../config/runtime';
import { useAuthoritativeTenantSlug } from '../../lib/tenantRequestContext';
import { fetchOnboardingTourState } from '../../lib/adminTools';
import {
  DndContext,
  DragOverlay,
  type DragStartEvent,
  type DragOverEvent,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  useDroppable,
  type DragEndEvent,
  type DragCancelEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  verticalListSortingStrategy,
  useSortable,
  arrayMove,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Grip, Plus, Sparkles } from 'lucide-react';
import { WIDGET_REGISTRY, getDefaultWidgetConfig } from '../../config/widgetRegistry';
import type { TimeRange, TimeOfDayInterval, PageState, WidgetSize } from '../../types/dashboard';
import { useDashboardTrades } from '../../hooks/useDashboardTrades';
import { buildDailyDataFromTrades, buildWeekSummaryFromTrades } from '../../lib/trades/authoritativeTradeData';
import { fmtMoney } from '../../utils/formatters';
import { buildSparseNativeSectionColumns, fitNativeSectionColumnsToCount } from '../layout/sectionLayoutPlacement';
import type { BillingAccessState } from '../../lib/billing';
import { filterEnabledWidgetIds, isWidgetEnabled } from '../../config/runtime/widgetGates';
import { buildTenantStorageKey, readTenantStorageItem, writeTenantStorageItem } from '../../lib/storage/tenantStorage';
import { WidgetCard } from '../ui/WidgetCard';
import TimeOfDayDropdown from '../ui/TimeOfDayDropdown';
import { SectionSeparator, AddSectionButton } from '../ui/SectionSeparator';
import NativeDashboardSectionGrid from './NativeDashboardSectionGrid';
import Tooltip from '../ui/TooltipSimple';
import WidgetsLauncher from '../ui/WidgetsLauncher';
import GlassClock from '../ui/GlassClock';
import TrialStatusBadge from '../billing/TrialStatusBadge';
import { BUILT_IN_PRESET_LAYOUTS } from '../../config/dashboardPresetDefaults';
import type { DashboardPresetLayout } from '../../config/dashboardPresetDefaults';
import { useDisplayTimezone } from '../app/DisplayTimezoneProvider';

const SortableSection: React.FC<{ id: string | number; isDragging: boolean; children: React.ReactElement }> = ({ id, children }) => {
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({
    id: String(id),
    data: { type: 'section', sectionId: id },
  });
  return (
    <div
      ref={setNodeRef}
      style={{
        transform: CSS.Transform.toString(transform),
        transition: transition ?? 'transform 220ms cubic-bezier(0.2, 0, 0, 1)',
      }}
      className="col-span-full overflow-visible"
    >
      {React.cloneElement(children, { dragHandleProps: { ...attributes, ...listeners } })}
    </div>
  );
};
import {
  buildDefaultRecentTradesColumns,
  sanitizeRecentTradesColumnVisibility,
} from '../../config/recentTradesColumns';
import { getDashboardDemoTradesForTenant } from '../../demo/demoData';
import { DemoTradesOverrideProvider, enableDemoMode, useDemoModeState, shouldUseDemoTrades } from '../../demo/demoMode';
import { filterTradesForDashboardRange } from '../../lib/trades/dashboardBucketAnalytics';
import { getDashboardWidgetScope } from '../../lib/dashboardWidgetScope';
import {
  getDashboardTimeRangeDays,
  hasIndependentDashboardTimeframe,
} from '../../lib/dashboardTimeRange';
import {
  buildDashboardTradeSourceSignature,
  hasCanonicalDashboardTrades,
} from '../../lib/trades/dashboardTradeSource';

// Allow users to resize widgets; these stay full-width in the native dashboard layout.
const FORCED_FULL_WIDTH_WIDGETS = new Set<string>(['openTrades', 'longTermInvestments']);
const BUILT_IN_PRESET_IDS = new Set<string>(['performance', 'trades', 'patterns', 'complete']);
const DASHBOARD_DEMO_TOGGLE_EVENT = 'tradeproof:dashboard-demo-toggle';
const DASHBOARD_WIDGET_EMPTY_MESSAGE = 'Import Trades to see the widget';

export const getWeekSummaryCardMotionClass = (isToday: boolean, isTotal: boolean) => (
  isToday && !isTotal ? 'z-20' : ''
);

const normalizeWidgetId = (widgetId: string) => (widgetId === 'recentTrades' ? 'openTrades' : widgetId);
const isWidgetSize = (value: unknown): value is WidgetSize => value === 'mini' || value === 'regular' || value === 'full' || value === 'large';
const getWidgetConfigEntry = (config: DashboardWidgetConfig, widgetId: string) => config[widgetId] ?? {};
const getWidgetVisible = (config: DashboardWidgetConfig, widgetId: string) => !!config[widgetId]?.visible;
const getWidgetSize = (config: DashboardWidgetConfig, widgetId: string): WidgetSize | undefined => {
  const size = config[widgetId]?.size;
  return isWidgetSize(size) ? size : undefined;
};

const ensureCoreWidgets = (order: string[]) => {
  const required = ['holdTimeAnalysis'];
  const updated = order.map(normalizeWidgetId);
  required.forEach((id) => {
    if (!updated.includes(id) && WIDGET_REGISTRY[id]) {
      updated.push(id);
    }
  });
  return updated;
};

const normalizeWidgetOrder = (order: string[]) => {
  const base = ensureCoreWidgets([...order]);
  const registryIds = filterEnabledWidgetIds(Object.keys(WIDGET_REGISTRY).filter((id) => id !== 'weekSummary'));
  registryIds.forEach((id) => {
    if (!base.includes(id)) base.push(id);
  });
  return filterEnabledWidgetIds(base);
};

const ensureHoldTimeConfig = (config: DashboardWidgetConfig): DashboardWidgetConfig => {
  const next: DashboardWidgetConfig = { ...config };
  const defaults = WIDGET_REGISTRY.holdTimeAnalysis as { size?: WidgetSize; title?: string } | undefined;
  const existing = next.holdTimeAnalysis || {};
  next.holdTimeAnalysis = {
    ...existing,
    visible: typeof existing.visible === 'boolean' ? existing.visible : true,
    size: existing.size || defaults?.size || 'mini',
    title: existing.title || defaults?.title || 'Hold Time Analysis',
  };
  return next;
};

class DashboardWidgetBoundary extends React.Component<
  { children: React.ReactNode },
  { hasError: boolean }
> {
  constructor(props: { children: React.ReactNode }) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    if ((import.meta as any).env?.DEV) {
      console.error('Dashboard widget failed to render', error, info);
    }
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="h-full flex items-center justify-center rounded-xl border border-dashed border-zinc-200 px-4 text-center text-sm text-zinc-500 dark:border-zinc-800 dark:text-zinc-400">
          {DASHBOARD_WIDGET_EMPTY_MESSAGE}
        </div>
      );
    }

    return this.props.children as React.ReactElement;
  }
}

const pruneWidgetConfigForRuntime = (config: DashboardWidgetConfig): DashboardWidgetConfig => {
  const next: DashboardWidgetConfig = { ...config };

  if (!isWidgetEnabled('openTrades')) {
    delete next.openTrades;
  }

  return ensureHoldTimeConfig(next);
};

const pruneWidgetIdsForRuntime = (widgetIds: string[]) => filterEnabledWidgetIds(Array.from(new Set((widgetIds || []).map(normalizeWidgetId))));

const pruneSectionsForRuntime = (sectionsToPrune: DashboardSection[]) => sectionsToPrune
  .map((section) => ({
    ...section,
    widgetIds: pruneWidgetIdsForRuntime(Array.isArray(section?.widgetIds) ? section.widgetIds : []),
  }))
  .filter((section) => Array.isArray(section.widgetIds) && section.widgetIds.length > 0);

const pruneSectionColumnsForRuntime = (sectionColumns: Record<string, string[][]>) => Object.entries(sectionColumns).reduce((acc, [sectionId, columns]) => {
  acc[String(sectionId)] = Array.isArray(columns)
    ? columns.map((column) => filterEnabledWidgetIds(Array.isArray(column) ? column.map(normalizeWidgetId) : []))
    : [];
  return acc;
}, {} as Record<string, string[][]>);

const buildDefaultOpenTradesColumns = buildDefaultRecentTradesColumns;

const buildDefaultWidgetConfig = (): DashboardWidgetConfig => {
  const defaultConfig = pruneWidgetConfigForRuntime(getDefaultWidgetConfig() as DashboardWidgetConfig);
  defaultConfig.averagePerShareGainLoss = {
    ...(defaultConfig.averagePerShareGainLoss || {}),
    size: 'mini',
  };
  return pruneWidgetConfigForRuntime({
    weekSummary: { visible: true, size: 'full', title: 'Week Summary' },
    ...defaultConfig,
  });
};

const buildDefaultWidgetOrder = () => {
  const order = normalizeWidgetOrder(Object.keys(WIDGET_REGISTRY));
  const idx = order.indexOf('openTrades');
  if (idx > -1) {
    order.splice(idx, 1);
    order.unshift('openTrades');
  }
  return order;
};

const buildDefaultSections = (order: string[]): DashboardSection[] => {
  const nonWeek = order.filter((id: string) => id !== 'weekSummary');
  const half = Math.ceil(nonWeek.length / 2) || 0;
  return [
    { id: 1, title: 'Performance Overview', isOpen: true, widgetIds: nonWeek.slice(0, half) },
    { id: 2, title: 'Risk Analysis', isOpen: true, widgetIds: nonWeek.slice(half) },
  ];
};

const getSectionDropId = (sectionId: string | number) => `section-${String(sectionId)}`;
const makeInstanceId = (sectionId: string | number, widgetId: string) => `inst__${String(sectionId)}__${widgetId}`;
const isInstanceId = (id: string) => typeof id === 'string' && id.startsWith('inst__');
const parseInstanceId = (id: string) => {
  if (!isInstanceId(id)) return null;
  const parts = id.split('__');
  if (parts.length < 3) return null;
  const sectionId = parts[1];
  const widgetId = parts.slice(2).join('__');
  return { sectionId, widgetId };
};
const normalizeDeep = (value: unknown): unknown => {
  if (Array.isArray(value)) return value.map(normalizeDeep);
  if (value && typeof value === 'object') {
    return Object.keys(value).sort().reduce((acc: Record<string, unknown>, key) => {
      acc[key] = normalizeDeep((value as Record<string, unknown>)[key]);
      return acc;
    }, {});
  }
  return value;
};
const normalizeLayoutForComparison = (value: unknown): unknown => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return normalizeDeep(value);

  const layout = { ...(value as Record<string, unknown>) };
  // widgetLocations duplicates the ordering already captured by sections. Older
  // built-in snapshots can omit forced full-width widgets from this metadata,
  // so comparing it directly makes an untouched preset look customized.
  delete layout.widgetLocations;

  if (Array.isArray(layout.order)) {
    layout.order = filterEnabledWidgetIds(layout.order.filter((id): id is string => typeof id === 'string').map(normalizeWidgetId));
  }
  if (Array.isArray(layout.favorites)) {
    layout.favorites = filterEnabledWidgetIds(layout.favorites.filter((id): id is string => typeof id === 'string').map(normalizeWidgetId));
  }
  if (Array.isArray(layout.widgets)) {
    layout.widgets = layout.widgets
      .filter((widget) => isWidgetEnabled(normalizeWidgetId(String((widget as { id?: unknown })?.id ?? ''))))
      .sort((left, right) =>
      String((left as { id?: unknown })?.id ?? '').localeCompare(String((right as { id?: unknown })?.id ?? ''))
    );
  }
  if (Array.isArray(layout.sections)) {
    layout.sections = layout.sections.map((section) => {
      if (!section || typeof section !== 'object') return section;
      const nextSection = { ...(section as Record<string, unknown>) };
      if (Array.isArray(nextSection.widgetIds)) {
        nextSection.widgetIds = filterEnabledWidgetIds(
          nextSection.widgetIds.filter((id): id is string => typeof id === 'string').map(normalizeWidgetId),
        );
      }
      return nextSection;
    });
  }
  if (layout.openTradesColumns && typeof layout.openTradesColumns === 'object') {
    // Normalize deprecated column IDs and defaults before comparing a saved
    // preset with its runtime representation.
    layout.openTradesColumns = sanitizeRecentTradesColumnVisibility(layout.openTradesColumns);
  }
  if (layout.sectionColumns && typeof layout.sectionColumns === 'object' && !Array.isArray(layout.sectionColumns)) {
    layout.sectionColumns = Object.entries(layout.sectionColumns as Record<string, unknown>).reduce((columns, [sectionId, value]) => {
      columns[sectionId] = Array.isArray(value)
        ? value.map((column) => Array.isArray(column)
          ? filterEnabledWidgetIds(column.filter((id): id is string => typeof id === 'string').map(normalizeWidgetId))
          : [])
        : value;
      return columns;
    }, {} as Record<string, unknown>);
  }

  return normalizeDeep(layout);
};
export const dashboardLayoutsEqual = (left: unknown, right: unknown) =>
  JSON.stringify(normalizeLayoutForComparison(left)) === JSON.stringify(normalizeLayoutForComparison(right));
const layoutsEqual = dashboardLayoutsEqual;
const isBuiltInPresetId = (presetId: string) => BUILT_IN_PRESET_IDS.has(String(presetId || '').trim());

const LEGACY_BUILT_IN_PRESET_SECTION_COLUMNS: Record<string, Record<string, string[][]>> = {
  performance: {
    1: [
      ['totalGainLoss', 'cumulativePnl'],
      ['averageDailyGainLoss', 'cumulativeDrawdown'],
      ['profitFactor', 'winningVsLosing'],
      ['largestGainLoss', 'averageTradePnl'],
    ],
  },
  complete: {
    sec1: [
      ['cumulativePnl', 'largestGain', 'totalGainLoss'],
      ['numberOfWinningTrades', 'maxConsecutiveWins', 'averageTradePnl'],
      ['numberOfLosingTrades', 'maxConsecutiveLosses', 'winningVsLosing', 'averageDailyGainLoss'],
      ['cumulativeDrawdown', 'largestLoss', 'largestGainLoss', 'profitFactor'],
    ],
  },
};

export const shouldMigrateLegacyBuiltInPresetLayout = (
  presetId: string,
  saved: SavedLayoutSnapshot | DashboardLayoutSnapshot | null | undefined,
) => {
  const legacyColumns = LEGACY_BUILT_IN_PRESET_SECTION_COLUMNS[String(presetId || '').trim()];
  if (!legacyColumns || !saved || !saved.sectionColumns || typeof saved.sectionColumns !== 'object' || Array.isArray(saved.sectionColumns)) {
    return false;
  }

  const sectionKey = presetId === 'performance'
    ? '1'
    : presetId === 'complete'
      ? 'sec1'
      : null;

  if (!sectionKey) return false;

  return layoutsEqual((saved.sectionColumns as Record<string, string[][]>)[sectionKey], legacyColumns[sectionKey]);
};

type DashboardWidgetConfigEntry = {
  visible?: boolean;
  size?: WidgetSize;
  title?: string;
  [key: string]: unknown;
};

type DashboardWidgetConfig = Record<string, DashboardWidgetConfigEntry>;

type DashboardSection = {
  id: number | string;
  title: string;
  isOpen: boolean;
  widgetIds: string[];
};

type SavedSectionLayout = {
  id?: number | string;
  title?: string;
  isOpen?: boolean;
  widgetIds?: unknown;
};

type SavedWidgetLocation = {
  id?: unknown;
  index?: unknown;
  sectionIndex?: unknown;
  sectionId?: unknown;
};

type SavedLayoutSnapshot = {
  cols?: unknown;
  order?: unknown;
  favorites?: unknown;
  openTradesColumns?: unknown;
  sectionColumns?: unknown;
  sections?: SavedSectionLayout[];
  widgetLocations?: SavedWidgetLocation[];
  widgets?: Array<{ id?: unknown; visible?: unknown; size?: unknown }>;
};

type DashboardLayoutSnapshot = {
  cols: ColCount;
  order: string[];
  widgetLocations?: Array<{ id: string; sectionId: number | string; sectionIndex: number; index: number }>;
  sectionColumns?: Record<string, string[][]>;
  widgets: Array<{ id: string; visible: boolean; size: WidgetSize }>;
  favorites: string[];
  openTradesColumns: Record<string, boolean>;
  sections: DashboardSection[];
};

export function restoreSectionsFromSavedLayout(saved: SavedLayoutSnapshot | null | undefined): DashboardSection[] {
  if (!saved || !Array.isArray(saved.sections)) return [];

  const allowedIds = new Set(Object.keys(WIDGET_REGISTRY));
  const referencedIds = new Set<string>();
  const markReferencedId = (rawId: unknown) => {
    if (typeof rawId !== 'string') return;
    const normalizedId = normalizeWidgetId(rawId);
    if (allowedIds.has(normalizedId)) {
      referencedIds.add(normalizedId);
    }
  };
  const dedupeIds = (ids: unknown[]) => {
    const seen = new Set<string>();
    const ordered: string[] = [];

    (ids || []).forEach((rawId) => {
      if (typeof rawId !== 'string') return;
      const normalizedId = normalizeWidgetId(rawId);
      if (!allowedIds.has(normalizedId) || seen.has(normalizedId)) return;
      seen.add(normalizedId);
      ordered.push(normalizedId);
    });

    return ordered;
  };

  if (Array.isArray(saved.order)) saved.order.forEach(markReferencedId);
  if (Array.isArray(saved.widgets)) saved.widgets.forEach((widget) => markReferencedId((widget as { id?: unknown } | null | undefined)?.id));
  if (Array.isArray(saved.widgetLocations)) saved.widgetLocations.forEach((location) => markReferencedId((location as { id?: unknown } | null | undefined)?.id));

  const restoreOpenTradesIfNeeded = (sections: DashboardSection[]) => {
    if (!referencedIds.has('openTrades')) return sections;
    if (!sections.length) return sections;
    if (sections.some((section) => section.widgetIds.includes('openTrades'))) return sections;

    return sections.map((section, index) => (
      index === 0
        ? { ...section, widgetIds: ['openTrades', ...section.widgetIds.filter((id) => id !== 'openTrades')] }
        : section
    ));
  };

  const baseSections: DashboardSection[] = saved.sections.map((section, index) => ({
    id: section?.id ?? (Date.now() + index),
    title: section?.title || 'Group',
    isOpen: typeof section?.isOpen === 'boolean' ? section.isOpen : true,
    widgetIds: dedupeIds(Array.isArray(section?.widgetIds) ? section.widgetIds : []),
  }));

  if (!Array.isArray(saved.widgetLocations) || !saved.widgetLocations.length) {
    return restoreOpenTradesIfNeeded(baseSections);
  }

  const locatedWidgetIds = new Set(
    saved.widgetLocations
      .map((location) => typeof location?.id === 'string' ? normalizeWidgetId(location.id) : null)
      .filter((id): id is string => !!id && allowedIds.has(id)),
  );
  const hasCompleteLocationMetadata = baseSections.every((section) =>
    section.widgetIds.every((widgetId) => locatedWidgetIds.has(widgetId)),
  );
  if (!hasCompleteLocationMetadata) {
    return restoreOpenTradesIfNeeded(baseSections);
  }

  const locationsBySection = new Map<string, Array<{ id: string; index: number; sectionIndex: number }>>();
  saved.widgetLocations.forEach((location) => {
    if (!location || typeof location.id !== 'string') return;
    const normalizedId = normalizeWidgetId(location.id);
    if (!allowedIds.has(normalizedId)) return;

    const sectionKey = String(location.sectionId);
    const entries = locationsBySection.get(sectionKey) || [];
    entries.push({
      id: normalizedId,
      index: Number.isFinite(location.index) ? Number(location.index) : Number.MAX_SAFE_INTEGER,
      sectionIndex: Number.isFinite(location.sectionIndex) ? Number(location.sectionIndex) : Number.MAX_SAFE_INTEGER,
    });
    locationsBySection.set(sectionKey, entries);
  });

  const sectionOrder = new Map<string, number>();
  const restoredSections = baseSections
    .map((section, fallbackSectionIndex) => {
      const sectionKey = String(section.id);
      const locatedEntries = (locationsBySection.get(sectionKey) || []).sort((left, right) => left.index - right.index);
      const orderedIds = dedupeIds(locatedEntries.map((entry) => entry.id)).filter((id: string) => section.widgetIds.includes(id));
      const remainingIds = section.widgetIds.filter((id: string) => !orderedIds.includes(id));

      const minSectionIndex = locatedEntries.reduce((currentMin, entry) => Math.min(currentMin, entry.sectionIndex), Number.MAX_SAFE_INTEGER);
      sectionOrder.set(sectionKey, Number.isFinite(minSectionIndex) && minSectionIndex !== Number.MAX_SAFE_INTEGER ? minSectionIndex : fallbackSectionIndex);

      return {
        ...section,
        widgetIds: [...orderedIds, ...remainingIds],
      };
    })
    .sort((left, right) => {
      const leftOrder = sectionOrder.get(String(left.id)) ?? 0;
      const rightOrder = sectionOrder.get(String(right.id)) ?? 0;
      return leftOrder - rightOrder;
    });

  return restoreOpenTradesIfNeeded(restoredSections);
}

interface DashboardPageProps {
  theme?: 'light' | 'dark';
  setTheme?: (theme: 'light' | 'dark') => void;
  onNavigateToJournalEntry?: (date: Date) => void;
  onOpenImportTrades?: () => void;
  onOpenSettings?: (initialTabLabel?: string) => void;
  onNavigate?: (page: string) => void;
  billingState?: BillingAccessState | null;
  billingReady?: boolean;
  layoutPresetsButtonRef?: React.RefObject<HTMLButtonElement>;
  dashboardUserKey?: string;
  isBootstrapping?: boolean;
  hasRealTrades?: boolean;
  demoModePermanentlyDisabled?: boolean;
  demoModeTemporarilyDisabled?: boolean;
  whiteLabelTenantSlug?: string | null;
  whiteLabelTenantReady?: boolean;
}

const SkeletonBlock = ({ className = '' }: { className?: string }) => (
  <div className={`animate-pulse rounded-xl ${className}`} />
);

const DashboardBootstrapSkeleton = ({ theme, sideMenuActive }: { theme: 'light' | 'dark'; sideMenuActive: boolean }) => {
  const shellCardClass = theme === 'dark'
    ? 'border-zinc-800 bg-zinc-900/85'
    : 'border-zinc-200 bg-white/90';
  const mutedBlockClass = theme === 'dark'
    ? 'bg-zinc-800/90'
    : 'bg-zinc-200/80';

  return (
    <div className={`min-w-0 scroll-stable overflow-y-auto overflow-x-hidden ${sideMenuActive ? 'pt-0' : ''}`}>
      <div className="mt-2 mb-4 grid grid-cols-1 items-center gap-3 pr-0 xl:grid-cols-3">
        <div className="space-y-3">
          <SkeletonBlock className={`h-10 w-52 ${mutedBlockClass}`} />
          <SkeletonBlock className={`h-4 w-72 ${mutedBlockClass}`} />
        </div>
        <div className="flex justify-start xl:mt-8 xl:justify-center">
          <div className={`flex items-center gap-4 rounded-2xl border px-4 py-3 ${shellCardClass}`}>
            <SkeletonBlock className={`h-10 w-10 rounded-full ${mutedBlockClass}`} />
            <SkeletonBlock className={`h-10 w-60 rounded-xl ${mutedBlockClass}`} />
            <SkeletonBlock className={`h-10 w-24 rounded-xl ${mutedBlockClass}`} />
          </div>
        </div>
        <div className="flex justify-end">
          <SkeletonBlock className={`h-10 w-32 rounded-xl ${mutedBlockClass}`} />
        </div>
      </div>

      <div className={`mb-4 rounded-2xl border px-4 py-4 ${shellCardClass}`}>
        <div className="flex flex-wrap items-center gap-3">
          <SkeletonBlock className={`h-7 w-24 rounded-full ${mutedBlockClass}`} />
          <SkeletonBlock className={`h-4 w-80 ${mutedBlockClass}`} />
          <SkeletonBlock className={`ml-auto h-9 w-24 rounded-xl ${mutedBlockClass}`} />
        </div>
      </div>

      <div className="grid gap-4 xl:grid-cols-3">
        {Array.from({ length: 6 }).map((_, index) => (
          <div key={index} className={`min-h-[250px] rounded-3xl border p-5 shadow-sm ${shellCardClass}`}>
            <div className="flex items-center justify-between gap-3">
              <SkeletonBlock className={`h-5 w-36 ${mutedBlockClass}`} />
              <SkeletonBlock className={`h-6 w-16 rounded-full ${mutedBlockClass}`} />
            </div>
            <SkeletonBlock className={`mt-5 h-40 w-full rounded-2xl ${mutedBlockClass}`} />
            <div className="mt-4 grid grid-cols-3 gap-3">
              <SkeletonBlock className={`h-10 rounded-xl ${mutedBlockClass}`} />
              <SkeletonBlock className={`h-10 rounded-xl ${mutedBlockClass}`} />
              <SkeletonBlock className={`h-10 rounded-xl ${mutedBlockClass}`} />
            </div>
          </div>
        ))}

        <div className={`col-span-full rounded-3xl border p-5 shadow-sm ${shellCardClass}`}>
          <div className="flex items-center justify-between gap-3">
            <SkeletonBlock className={`h-5 w-40 ${mutedBlockClass}`} />
            <SkeletonBlock className={`h-9 w-28 rounded-xl ${mutedBlockClass}`} />
          </div>
          <div className="mt-5 space-y-3">
            {Array.from({ length: 4 }).map((_, rowIndex) => (
              <div key={rowIndex} className="grid grid-cols-5 gap-3">
                <SkeletonBlock className={`h-12 rounded-xl ${mutedBlockClass}`} />
                <SkeletonBlock className={`h-12 rounded-xl ${mutedBlockClass}`} />
                <SkeletonBlock className={`h-12 rounded-xl ${mutedBlockClass}`} />
                <SkeletonBlock className={`h-12 rounded-xl ${mutedBlockClass}`} />
                <SkeletonBlock className={`h-12 rounded-xl ${mutedBlockClass}`} />
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default function DashboardPage({
  theme = 'light',
  onNavigateToJournalEntry,
  onOpenSettings,
  onNavigate,
  billingState = null,
  billingReady = false,
  layoutPresetsButtonRef: externalLayoutPresetsButtonRef,
  dashboardUserKey,
  isBootstrapping = false,
  demoModePermanentlyDisabled = false,
  demoModeTemporarilyDisabled = false,
  whiteLabelTenantSlug = null,
  whiteLabelTenantReady = true,
}: DashboardPageProps) {
  const displayTimeZone = useDisplayTimezone();
  // Dashboard analytics must use the same current repository view as the
  // Trades page. This refreshes after imports and does not retain stale cached
  // persisted CSV data from a previous import state.
  const realAuthoritativeTrades = useDashboardTrades();
  const demoMode = useDemoModeState();
  const showDemoUi = true;
  const authoritativeTenantSlug = useAuthoritativeTenantSlug();
  const demoTrades = useMemo(() => getDashboardDemoTradesForTenant(authoritativeTenantSlug || currentRuntime.tenant.id), [authoritativeTenantSlug, currentRuntime.tenant.id]);
  const isDemoOrDisabled = demoModePermanentlyDisabled || demoModeTemporarilyDisabled;
  const hasCanonicalRealTrades = hasCanonicalDashboardTrades(realAuthoritativeTrades);
  const demoModeActive = shouldUseDemoTrades({
    isDemoMode: demoMode.enabled,
    accountType: isDemoOrDisabled ? null : (billingState?.isAdminBillingBypass === true ? 'bypass' : null),
    hasRealTrades: hasCanonicalRealTrades,
  });
  console.log('[DashboardPage Render]', {
    demoModeEnabled: demoMode.enabled,
    demoModeTemporarilyDisabled,
    demoModePermanentlyDisabled,
    isDemoOrDisabled,
    demoModeActive,
  });
  const authoritativeTrades = demoModeActive ? demoTrades : realAuthoritativeTrades;
  const dashboardTradeSourceSignature = useMemo(
    () => buildDashboardTradeSourceSignature(authoritativeTrades),
    [authoritativeTrades]
  );
  useEffect(() => {
    if (!import.meta.env?.DEV) return;
    console.log('Billing state:', billingState);
  }, [billingState]);
  const USE_DND_KIT_GRID = false;
  const USE_NATIVE_DASHBOARD_GRID = true;
  // Grid row sizing: set ROW_HEIGHT and gap so mini=152/180/...? using spans 3/6/9
  // Chosen constants: ROW_HEIGHT=40, GRID_GAP_PX=16. Spans: mini=3, regular=6, large=9
  const ROW_HEIGHT = 40;
  const GRID_GAP_PX = 16;
  const ACTIVE_PRESET_KEY_BASE = 'dashboard:active-layout-preset';
  const ACTIVE_PRESET_KEY_LEGACY = 'dashboard:active-layout-preset';
  const userScope = (dashboardUserKey || 'guest').trim() || 'guest';
  const ACTIVE_PRESET_KEY = buildTenantStorageKey(ACTIVE_PRESET_KEY_BASE, userScope);
  const makePresetKey = useCallback((base: string, presetId: string) => buildTenantStorageKey(base, userScope, presetId), [userScope]);
  const makeLegacyPresetKey = useCallback((base: string, presetId: string) => `${base}:${presetId}`, []);
  const readPresetItem = useCallback((base: string, presetId: string) => readTenantStorageItem(makePresetKey(base, presetId), [makeLegacyPresetKey(base, presetId), base]), [makePresetKey, makeLegacyPresetKey]);
  const writePresetItem = useCallback((base: string, presetId: string, value: string) => {
    writeTenantStorageItem(makePresetKey(base, presetId), value);
  }, [makePresetKey]);
  const removePresetItem = useCallback((base: string, presetId: string) => {
    try {
      localStorage.removeItem(makePresetKey(base, presetId));
      localStorage.removeItem(makeLegacyPresetKey(base, presetId));
      localStorage.removeItem(base);
    } catch {}
  }, [makeLegacyPresetKey, makePresetKey]);
  const readActivePresetId = () => {
    try {
      const raw = readTenantStorageItem(ACTIVE_PRESET_KEY, [ACTIVE_PRESET_KEY_LEGACY]);
      if (raw && typeof raw === 'string') return raw;
    } catch {}
    return 'complete';
  };

  // Detect whether a side menu/sidebar is present so we can adjust top spacing
  const [sideMenuActive, setSideMenuActive] = useState<boolean>(() => {
    try {
      return !!document?.querySelector('.sidebar-root');
    } catch {
      return false;
    }
  });

  useEffect(() => {
    try { setSideMenuActive(!!document.querySelector('.sidebar-root')); } catch {}
    const obs = new MutationObserver(() => {
      try { setSideMenuActive(!!document.querySelector('.sidebar-root')); } catch {}
    });
    obs.observe(document.documentElement, { attributes: true, childList: true, subtree: true });
    window.addEventListener('resize', () => {
      try { setSideMenuActive(!!document.querySelector('.sidebar-root')); } catch {}
    });
    return () => { obs.disconnect(); window.removeEventListener('resize', () => {}); };
  }, [removePresetItem]);

  const [activePresetId, setActivePresetId] = useState<string>(() => readActivePresetId());
  const presetSavedLayoutKey = makePresetKey('dashboard:saved-layout', activePresetId);
  const presetFavoritesKey = makePresetKey('dashboard:widget-favorites', activePresetId);
  const presetWidgetDropdownKey = makePresetKey('widget-dropdown:selected', activePresetId);

  useEffect(() => {
    try {
      if (activePresetId) {
        localStorage.setItem(ACTIVE_PRESET_KEY, activePresetId);
        localStorage.setItem(ACTIVE_PRESET_KEY_LEGACY, activePresetId);
      }
    } catch {}
  }, [activePresetId, ACTIVE_PRESET_KEY, ACTIVE_PRESET_KEY_LEGACY]);

  const [forceOpenTour, setForceOpenTour] = useState(() => {
    try {
      const shouldForce = sessionStorage.getItem('onboarding.tour.forceLaunch') === 'true';
      if (shouldForce) {
        sessionStorage.removeItem('onboarding.tour.forceLaunch');
        return true;
      }
    } catch {}
    return false;
  });

  // Admin Tools can launch or reset the onboarding tour for this user through the
  // server. Consume the flag once (authenticated users only) and let the existing
  // tour logic force-open after clearing this user's completion marker.
  useEffect(() => {
    if (forceOpenTour) {
      window.dispatchEvent(new CustomEvent('tradeproof:restart-product-tour'));
      setForceOpenTour(false);
    }
  }, [forceOpenTour]);

  useEffect(() => {
    const userKey = String(dashboardUserKey || '').trim();
    if (!userKey || userKey === 'guest' || !whiteLabelTenantReady) return;

    let cancelled = false;
    fetchOnboardingTourState()
      .then((state) => {
        if (cancelled || !state?.ok) return;
        if (!state.launched && !state.reset) return;

        try {
          const onboardingScope = String(whiteLabelTenantSlug || currentRuntime.brand.id).trim().toLowerCase() || currentRuntime.brand.id;
          localStorage.removeItem(`onboarding.tour.completed.${onboardingScope}.${userKey}`);
        } catch {}
        setForceOpenTour(true);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [dashboardUserKey, whiteLabelTenantReady, whiteLabelTenantSlug]);
  const [widgetConfig, setWidgetConfig] = useState<DashboardWidgetConfig>(() => buildDefaultWidgetConfig());
  const [widgetOrder, setWidgetOrder] = useState<string[]>(() => {
    const canonical = BUILT_IN_PRESET_LAYOUTS[activePresetId];
    if (canonical) {
      return normalizeWidgetOrder(canonical.order.map(normalizeWidgetId));
    }
    const savedOrder = readPresetItem('dashboardWidgetOrder', activePresetId);
    if (savedOrder) {
      try {
        return normalizeWidgetOrder(JSON.parse(savedOrder));
      } catch {
        // fall through to default order
      }
    }
    return buildDefaultWidgetOrder();
  });
  const [timeRange, setTimeRange] = useState<TimeRange>(() => {
    try {
      const raw = readPresetItem('dashboard:time-range', activePresetId);
      if (raw === 'week' || raw === 'month' || raw === 'year') return raw;
    } catch {}
    return 'month';
  });
  const [timeRangeSelectionRevision, setTimeRangeSelectionRevision] = useState(0);
  const handleDashboardTimeRangeSelect = useCallback((nextTimeRange: TimeRange) => {
    setTimeRange(nextTimeRange);
    try {
      writePresetItem('dashboard:time-range', activePresetId, nextTimeRange);
    } catch {}
    setTimeRangeSelectionRevision((revision) => revision + 1);
  }, [activePresetId, writePresetItem]);
  const timeRangeRef = useRef<TimeRange>(timeRange);
  useEffect(() => {
    timeRangeRef.current = timeRange;
  }, [timeRange]);
  const [timeOfDayInterval, setTimeOfDayInterval] = useState<TimeOfDayInterval>('30min');
  // Layout column count state - load from localStorage if available
  const [cols, setCols] = useState<ColCount>(() => {
    const canonical = BUILT_IN_PRESET_LAYOUTS[activePresetId];
    if (canonical && [2, 3, 4, 5].includes(canonical.cols)) {
      return canonical.cols as ColCount;
    }
    const saved = readPresetItem('dashboardLayoutCols', activePresetId);
    if (saved) {
      const parsed = parseInt(saved, 10);
      if ([2, 3, 4, 5].includes(parsed)) return parsed as ColCount;
    }
    return 4;
  });
  const [dashboardViewportWidth, setDashboardViewportWidth] = useState(() => (
    typeof window === 'undefined' ? 1280 : window.innerWidth
  ));
  useEffect(() => {
    const updateDashboardViewportWidth = () => setDashboardViewportWidth(window.innerWidth);
    window.addEventListener('resize', updateDashboardViewportWidth);
    return () => window.removeEventListener('resize', updateDashboardViewportWidth);
  }, []);
  const isTabletDashboard = dashboardViewportWidth < 1280;
  const [draggedWidgetId, setDraggedWidgetId] = useState<string | null>(null);
  const [dragPlaceholder, setDragPlaceholder] = useState<{ sectionId: string | number; index: number } | null>(null);
  const [draggedSize, setDraggedSize] = useState<'full' | 'regular' | 'mini' | 'large' | null>(null);
  const [recentlyInsertedSectionId, setRecentlyInsertedSectionId] = useState<string | number | null>(null);
  const layoutMode = true;
  // Old inline widgets menu replaced by WidgetsLauncher
  const draggedWidget = useRef<string | null>(null);
  const dashboardRef = useRef<HTMLDivElement>(null);
  const defaultLayoutPresetsButtonRef = useRef<HTMLButtonElement>(null);
  const layoutPresetsButtonRef = externalLayoutPresetsButtonRef ?? defaultLayoutPresetsButtonRef;
  const [widgetsLauncherOpen, setWidgetsLauncherOpen] = useState(false);
  const [widgetsLauncherTargetSectionId, setWidgetsLauncherTargetSectionId] = useState<string | number | null>(null);
  // Open Trades column visibility (mirrors behavior of the Widgets menu toggles)
  const [openTradesColumns, setOpenTradesColumns] = useState(() => buildDefaultOpenTradesColumns());
  const [openTradesBanner, setOpenTradesBanner] = useState<React.ReactNode | null>(null);

  const [pageState, setPageState] = useState<PageState>({});
  // Ref to stabilize stacked-placeholder hover decisions (prevent rapid top/bottom flips)
  const stackedHoverRef = useRef<{ sectionId: string | number; insertIndex: number; lastIdx: number | null; lastY: number | null } | null>(null);

  // Local hover state for the "mini next to large with adjacent regular" composite.
  // This drives the visual rotation between [mini, regular] and [regular, mini] without touching
  // the global placeholder index (which tends to cause flicker).
  const [largeAdjRegMiniHover, setLargeAdjRegMiniHover] = useState<{
    sectionId: string | number;
    insertIndex: number;
    mode: 'miniTop' | 'miniBottom';
  } | null>(null);
  const largeAdjRegMiniRafRef = useRef<number | null>(null);

  const dndSensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 0 },
    })
  );

  const SectionDroppable: React.FC<{ sectionId: string | number; className?: string; style?: React.CSSProperties; children: React.ReactNode }> = ({
    sectionId,
    className,
    style,
    children,
  }) => {
    const dropId = getSectionDropId(sectionId);
    const { setNodeRef, isOver } = useDroppable({ id: dropId });
    return (
      <div
        ref={setNodeRef}
        className={className}
        style={style}
        data-section-droppable={dropId}
        data-section-over={isOver ? 'true' : 'false'}
      >
        {children}
      </div>
    );
  };

  const SortableGridItem: React.FC<{ id: string; rowSpan: number; isFullWidth: boolean; children: React.ReactNode }> = ({
    id,
    rowSpan,
    isFullWidth,
    children,
  }) => {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
    // dnd-kit sortable strategies may apply scaleX/scaleY during layout animation.
    // Force scale to 1 so the dragged widget shape doesn't change.
    const stableTransform = transform ? { ...transform, scaleX: 1, scaleY: 1 } : transform;
    // Compute explicit pixel height so the item's shape remains constant while dragging.
    const pixelHeight = rowSpan * ROW_HEIGHT + Math.max(0, rowSpan - 1) * GRID_GAP_PX;
    const style: React.CSSProperties = {
      transform: CSS.Transform.toString(stableTransform),
      transition: isDragging ? undefined : (isFullWidth ? undefined : transition),
      gridRow: `span ${rowSpan}`,
      height: `${pixelHeight}px`,
      minHeight: `${pixelHeight}px`,
      ...(isFullWidth ? { gridColumn: '1 / -1' } : null),
      opacity: isDragging ? 0.08 : undefined,
      zIndex: isDragging ? 50 : undefined,
      touchAction: 'none',
      position: 'relative',
      willChange: 'transform',
    };
    // Pass dnd-kit attributes/listeners into the child so we can attach them to a handle
    const child = React.isValidElement(children)
      ? React.cloneElement(children as React.ReactElement, { dragListeners: listeners, dragAttributes: attributes, domId: id })
      : children;

    return (
      <div ref={setNodeRef} style={style} {...attributes} {...listeners}>
        {child}
      </div>
    );
  };

  // Listen for global 'open-widgets-launcher' events as a fallback (SectionSeparator dispatches this)
  useEffect(() => {
    const handler = (e: Event) => {
      try {
        const detail = (e as CustomEvent).detail;
        if (typeof detail === 'string' || typeof detail === 'number' || detail === null) {
          setWidgetsLauncherTargetSectionId(detail);
          setWidgetsLauncherOpen(true);
        }
      } catch {}
    };
    window.addEventListener('open-widgets-launcher', handler as EventListener);
    return () => window.removeEventListener('open-widgets-launcher', handler as EventListener);
  }, [activePresetId]);

  // Prevent duplicate handling of the same dnd-kit dragEnd (debounce key)
  const lastProcessedDndRef = useRef<string | null>(null);
  const [dragOverlayState, setDragOverlayState] = useState<null | { instanceId: string; width: number; height: number }>(null);
  const lastDndReorderRef = useRef<{ activeId: string; overId: string; at: number } | null>(null);

  // Broadcast current cols so LayoutPresetsMenu can highlight the active grid when mounted elsewhere.
  useEffect(() => {
    try {
      window.dispatchEvent(new CustomEvent('dashboard:cols-changed', { detail: { cols } }));
    } catch {}
  }, [cols]);

  // NOTE: the live clock updates inside `GlassClock` to avoid re-rendering the whole dashboard every second.

  const getActualWidgetSize = useCallback((widgetId: string): 'full' | 'regular' | 'mini' | 'large' => {
    if (FORCED_FULL_WIDTH_WIDGETS.has(widgetId)) return 'full';
    const saved = getWidgetSize(widgetConfig, widgetId);
    return saved || 'regular';
  }, [widgetConfig]);

  const savedNativeColumnCount = (typeof cols === 'number' && [2, 3, 4, 5].includes(cols as number)) ? (cols as number) : 4;
  // Tablet reflow is a render-time constraint only. Keep the saved desktop
  // preference intact so crossing the breakpoint never rewrites user layouts.
  const nativeColumnCount = isTabletDashboard ? 2 : savedNativeColumnCount;

  useEffect(() => {
    const frame = window.requestAnimationFrame(() => {
      window.dispatchEvent(new Event('resize'));
    });
    return () => window.cancelAnimationFrame(frame);
  }, [nativeColumnCount]);

  const buildNativeSectionColumns = useCallback((sectionWidgetIds: string[], config: DashboardWidgetConfig) => {
    const visibleIds = sectionWidgetIds.filter((wid) => getWidgetVisible(config, wid));
    const gridVisibleIds = visibleIds.filter((wid) => {
      const size = FORCED_FULL_WIDTH_WIDGETS.has(wid) ? 'full' : getWidgetSize(config, wid) || 'regular';
      return size !== 'full';
    });
    const getNativeWidgetSize = (widgetId: string): 'mini' | 'regular' | 'large' => {
      const size = FORCED_FULL_WIDTH_WIDGETS.has(widgetId) ? 'full' : getWidgetSize(config, widgetId) || 'regular';
      return size === 'full' ? 'large' : size;
    };
    return buildSparseNativeSectionColumns(gridVisibleIds, nativeColumnCount, getNativeWidgetSize);
  }, [nativeColumnCount]);

  const getCanonicalRuntimeSectionColumns = useCallback((canonical: DashboardPresetLayout) => {
    if (nativeColumnCount === canonical.cols || !canonical.sectionColumns) {
      return canonical.sectionColumns ?? {};
    }
    const canonicalWidgetConfig = buildDefaultWidgetConfig();
    canonical.widgets.forEach((widget) => {
      if (!widget || typeof widget.id !== 'string') return;
      const widgetId = normalizeWidgetId(widget.id);
      canonicalWidgetConfig[widgetId] = {
        ...getWidgetConfigEntry(canonicalWidgetConfig, widgetId),
        visible: widgetId === 'weekSummary' ? true : !!widget.visible,
        ...(isWidgetSize(widget.size) ? { size: widget.size } : {}),
      };
    });
    if (!canonicalWidgetConfig.weekSummary) canonicalWidgetConfig.weekSummary = { visible: true, size: 'full', title: 'Week Summary' };
    canonicalWidgetConfig.weekSummary.visible = true;
    const runtimeWidgetConfig = pruneWidgetConfigForRuntime(ensureHoldTimeConfig(canonicalWidgetConfig));
    return Object.entries(canonical.sectionColumns).reduce((columns, [sectionId, savedColumns]) => {
      const runtimeColumns = buildNativeSectionColumns(Array.isArray(savedColumns) ? savedColumns.flat() : [], runtimeWidgetConfig);
      columns[sectionId] = runtimeColumns.map((column) => column.map((card) => card.id));
      return columns;
    }, {} as Record<string, string[][]>);
  }, [buildNativeSectionColumns, nativeColumnCount]);

  // Throttle placeholder updates to animation frames to avoid rapid state churn
  const placeholderUpdateRef = useRef<{ frame: number | null; pending: { sectionId: string | number; index: number } | null }>({ frame: null, pending: null });

  useEffect(() => {
    const currentRef = placeholderUpdateRef.current;
    return () => {
      try {
        if (currentRef.frame) cancelAnimationFrame(currentRef.frame);
      } catch {}
    };
  }, []);

  const updatePlaceholder = useCallback((sectionId: string | number, index: number) => {
    const r = placeholderUpdateRef.current;
    r.pending = { sectionId, index };
    if (r.frame == null) {
      r.frame = requestAnimationFrame(() => {
        const pending = placeholderUpdateRef.current.pending;
        placeholderUpdateRef.current.frame = null;
        placeholderUpdateRef.current.pending = null;
        if (!pending) return;
        setDragPlaceholder(prev => {
          if (prev && prev.sectionId === pending.sectionId && prev.index === pending.index) return prev;
          return { sectionId: pending.sectionId, index: pending.index };
        });
      });
    }
  }, []);

  // Ensure the "Open Trades" widget is present and visible when the dashboard loads
  useEffect(() => {
    if (!isWidgetEnabled('openTrades')) {
      setWidgetConfig(prev => {
        if (!prev.openTrades) return prev;
        const next: DashboardWidgetConfig = { ...prev };
        delete next.openTrades;
        return next;
      });
      setWidgetOrder((prev: string[]) => prev.filter((id) => id !== 'openTrades'));
      setFavoriteWidgets((prev) => prev.filter((id) => id !== 'openTrades'));
      setSections((prev) => prev
        .map((section) => ({ ...section, widgetIds: section.widgetIds.filter((id) => id !== 'openTrades') }))
        .filter((section) => section.widgetIds.length > 0));
      setSectionColumnsById((prev) => {
        const next = { ...prev };
        let changed = false;

        for (const [sectionId, columns] of Object.entries(next)) {
          const filteredColumns = columns.map((column) => column.filter((id) => id !== 'openTrades'));
          if (JSON.stringify(filteredColumns) !== JSON.stringify(columns)) {
            next[sectionId] = filteredColumns;
            changed = true;
          }
          if (!filteredColumns.some((column) => column.length > 0)) {
            delete next[sectionId];
            changed = true;
          }
        }

        return changed ? next : prev;
      });
      return;
    }

    // Make sure openTrades is visible
    setWidgetConfig(prev => ({
      ...prev,
      openTrades: { ...(prev.openTrades || {}), visible: true }
    }));
    // Make sure openTrades exists in the order (at the top if missing)
    setWidgetOrder((prev: string[]) => (prev.includes('openTrades') ? prev : ['openTrades', ...prev]));
  }, []);

  const setPage = (key: string, value: number | ((p: number) => number)) =>
    setPageState(prev => ({ ...prev, [key]: typeof value === 'function' ? value(prev[key] || 0) : value }));

  const resetPages = () => setPageState({});

  useEffect(() => resetPages(), [timeRange, timeOfDayInterval]);

  // Widget order and grid column persistence are handled through the full layout snapshot.
  useEffect(() => {}, [widgetOrder, cols]);


  const dashboardPeriodTrades = useMemo(
    () => filterTradesForDashboardRange(authoritativeTrades as Record<string, any>[], timeRange),
    [authoritativeTrades, timeRange]
  );

  const dailyData = useMemo(
    () => buildDailyDataFromTrades(
      dashboardPeriodTrades as Parameters<typeof buildDailyDataFromTrades>[0],
      getDashboardTimeRangeDays(timeRange)
    ),
    [dashboardPeriodTrades, timeRange]
  );

  const independentTimeframeDailyData = useMemo(
    () => buildDailyDataFromTrades(
      authoritativeTrades as Parameters<typeof buildDailyDataFromTrades>[0],
      366
    ),
    [authoritativeTrades]
  );

  useEffect(() => {
    const styleId = 'dashboard-drag-styles';
    let styleElement = document.getElementById(styleId);
    if (!styleElement) {
      styleElement = document.createElement('style');
      styleElement.id = styleId;
      document.head.appendChild(styleElement);
    }
    styleElement.innerHTML = `
      .widget-card.dragging {
        opacity: 0.35;
        border-style: dashed;
        transform: scale(0.98);
      }
    `;
    return () => {
      const el = document.getElementById(styleId);
      if (el) {
        el.remove();
      }
    };
  }, []);

  // click-away handled inside WidgetsLauncher

  const toggleWidget = (widgetName: string) => {
    widgetName = normalizeWidgetId(widgetName);
    // `weekSummary` must never be cleared - protect it from toggles
    if (widgetName === 'weekSummary') return;

    const currentlyVisible = !!widgetConfig[widgetName]?.visible;
    setWidgetConfig(prev => {
      const wasVisible = !!prev[widgetName]?.visible;
      // debug logging removed for performance

      const next: DashboardWidgetConfig = {
        ...prev,
        [widgetName]: { ...getWidgetConfigEntry(prev, widgetName), visible: !wasVisible }
      };

      // If we're turning the widget on and it's not present in the global order, add it
      if (!wasVisible) {
        setWidgetOrder(orderPrev => {
          const nextOrder = orderPrev.includes(widgetName) ? orderPrev : [...orderPrev, widgetName];
          // debug logging removed for performance
          return nextOrder;
        });
      }

      return next;
    });

    // If the widget was visible and is being turned off, remove it from favorites
    if (currentlyVisible) {
      setFavoriteWidgets(prev => {
        const nextFavs = prev.filter(id => id !== widgetName);
        if (!isBuiltInPresetId(activePresetIdRef.current)) {
          try { localStorage.setItem(presetFavoritesKey, JSON.stringify(nextFavs)); } catch {}
        }
        return nextFavs;
      });
    }
  };

  const handleClearAll = () => {
    // Clear all widgets except `weekSummary` which must remain visible
    setWidgetConfig(prev => {
      const newConfig = { ...prev };
      Object.keys(newConfig).forEach(key => {
        newConfig[key].visible = key === 'weekSummary';
      });
      return newConfig;
    });
  };

  const handleSelectAll = () => {
    // Select all widgets (weekSummary remains visible as well)
    setWidgetConfig(prev => {
      const newConfig = { ...prev };
      Object.keys(newConfig).forEach(key => {
        newConfig[key].visible = true;
      });
      return newConfig;
    });
  };

  const [favoriteWidgets, setFavoriteWidgets] = useState<string[]>([]);

  // Sections state: map groups of widget IDs into named sections.
  const [sections, setSections] = useState<DashboardSection[]>(() => {
    return buildDefaultSections(widgetOrder);
  });
  const [activeSectionId, setActiveSectionId] = useState<string | null>(null);
  const [sectionColumnsById, setSectionColumnsById] = useState<Record<string, string[][]>>({});

  const removeWidgetFromSections = useCallback((sectionList: DashboardSection[], widgetId: string, keepSectionId?: string | number) => {
    const targetKeepId = typeof keepSectionId === 'undefined' ? null : String(keepSectionId);
    return sectionList.map((section) => {
      if (targetKeepId !== null && String(section.id) === targetKeepId) return section;
      const nextWidgetIds = section.widgetIds.filter((id) => String(id) !== String(widgetId));
      return nextWidgetIds.length === section.widgetIds.length ? section : { ...section, widgetIds: nextWidgetIds };
    });
  }, []);

  const handleDndDragEnd = useCallback((event: DragEndEvent) => {
    const DND_DEBUG = typeof window !== 'undefined' && localStorage.getItem('dashboard:dnd-debug') === 'true';
    const { active, over } = event;
    if (!over) return;
    const activeId = String(active.id);
    const overId = String(over.id);
    // dedupe rapid/duplicate calls from multiple contexts/sensors
    try {
      const key = `${activeId}::${overId}`;
      if (lastProcessedDndRef.current === key) {
        if (DND_DEBUG) console.warn('[DND DEBUG] duplicate dragEnd ignored', key);
        return;
      }
      lastProcessedDndRef.current = key;
      setTimeout(() => { if (lastProcessedDndRef.current === key) lastProcessedDndRef.current = null; }, 250);
    } catch {}
    if (DND_DEBUG) {
      try {
        const el = (over as { element?: HTMLElement | null }).element ?? null;
        const overElInfo = el ? { tag: el.tagName, id: el.id, class: el.className, dataset: { ...(el.dataset || {}) } } : null;
        console.groupCollapsed('[DND DEBUG] dragEnd');
        console.log('activeId', activeId);
        console.log('overId', overId);
        console.log('over element', overElInfo);
        console.log('sections snapshot', sections.map(s => ({ id: s.id, widgetIds: s.widgetIds.length })));
        console.log('widgetConfig snapshot', Object.keys(widgetConfig).reduce((acc, k) => { acc[k] = { visible: widgetConfig[k]?.visible, size: widgetConfig[k]?.size }; return acc; }, {} as Record<string, { visible?: boolean; size?: WidgetSize }>));
        console.groupEnd();
      } catch (err) {
        console.warn('[DND DEBUG] failed to log drag end', err);
      }
    }
    if (activeId === overId) return;

    const findSectionIdForWidget = (wid: string) => {
      const hit = sections.find(s => s.widgetIds.includes(wid));
      return hit ? String(hit.id) : null;
    };

    const findSectionIdForElement = (el: HTMLElement | null) => {
      try {
        if (!el) return null;
        const sd = el.closest('[data-section-droppable]') as HTMLElement | null;
        if (!sd) return null;
        const attr = sd.getAttribute('data-section-droppable');
        if (!attr) return null;
        // attr is like 'section-<id>'
        return attr.startsWith('section-') ? attr.slice('section-'.length) : attr;
      } catch {
        return null;
      }
    };

    // Map instance ids back to widget ids and section ids when present
    const activeInstance = parseInstanceId(activeId);
    const activeWidgetId = activeInstance ? activeInstance.widgetId : activeId;
    const sourceSectionId = activeInstance ? activeInstance.sectionId : findSectionIdForWidget(activeWidgetId);

    let destSectionId: string | null = null;
    let overWidgetId: string | null = null;
    if (overId.startsWith('section-')) {
      destSectionId = overId.slice('section-'.length);
    } else if (isInstanceId(overId)) {
      const parsed = parseInstanceId(overId);
      if (parsed) {
        destSectionId = parsed.sectionId;
        overWidgetId = parsed.widgetId;
      }
    } else {
      overWidgetId = overId;
      destSectionId = findSectionIdForWidget(overWidgetId);
    }

    // If we couldn't find a destination section via id mapping, try resolving via the DOM
    const overElement = 'element' in over ? (over as { element?: HTMLElement | null }).element ?? null : null;
    if (!destSectionId && overElement) {
      const el = overElement;
      const resolved = findSectionIdForElement(el);
      if (resolved) destSectionId = resolved;
    }

    if (!sourceSectionId || !destSectionId) {
      if (DND_DEBUG) console.warn('[DND DEBUG] could not resolve source/dest', { sourceSectionId, destSectionId, activeId, overId });
      return;
    }

    setSections(prev => {
      const next = prev.map(s => ({ ...s, widgetIds: [...s.widgetIds] }));
      const srcIdx = next.findIndex(s => String(s.id) === sourceSectionId);
      const dstIdx = next.findIndex(s => String(s.id) === destSectionId);
      if (srcIdx === -1 || dstIdx === -1) return prev;

      // Remove the widget from every section first so the move is exclusive.
      next.forEach((section, index) => {
        if (index === dstIdx) return;
        section.widgetIds = section.widgetIds.filter(id => String(id) !== String(activeWidgetId));
      });

      // Remove from source as well in case the source and destination resolve to the same section.
      next[srcIdx].widgetIds = next[srcIdx].widgetIds.filter(id => String(id) !== String(activeWidgetId));

      // Insert into destination
      const destVisible = next[dstIdx].widgetIds.filter((wid) => widgetConfig[wid]?.visible);
      const overIdx = overWidgetId ? destVisible.indexOf(overWidgetId) : -1;
      const insertAt = destSectionId === String(next[dstIdx].id) && !overWidgetId ? destVisible.length : (overIdx === -1 ? destVisible.length : overIdx);
      const destVisibleNext = [...destVisible];
      destVisibleNext.splice(insertAt, 0, activeWidgetId);

      const destHidden = next[dstIdx].widgetIds.filter((wid) => !widgetConfig[wid]?.visible);
      next[dstIdx].widgetIds = [...destVisibleNext, ...destHidden];

      return next;
    });

    setWidgetOrder(prev => {
      const next = [...prev];
      // remove only the first occurrence of the active widget id
      const removeIdx = next.indexOf(activeWidgetId);
      if (removeIdx !== -1) next.splice(removeIdx, 1);

      if (destSectionId && destSectionId === sourceSectionId && !overWidgetId) {
        // moving within same section to end
        next.push(activeWidgetId);
        return next;
      }

      if (!overWidgetId) {
        next.push(activeWidgetId);
        return next;
      }

      const overIndex = next.indexOf(overWidgetId);
      if (overIndex === -1) {
        next.push(activeWidgetId);
        return next;
      }
      next.splice(overIndex, 0, activeWidgetId);
      return next;
    });

    // Clear any lingering visual dragging state (dnd-kit path doesn't call the native drag end)
    try {
      document.querySelectorAll('.widget-card.dragging').forEach(el => el.classList.remove('dragging'));
    } catch {}
    try { draggedWidget.current = null; } catch {}
    try { setDraggedWidgetId(null); } catch {}
    try { setDragOverWidget(null); } catch {}
    try { setDragPlaceholder(null); } catch {}
    try { setDraggedSize(null); } catch {}
    try { setDragOverlayState(null); } catch {}
    try { (window as Window & { __dashboard_dnd_active?: boolean }).__dashboard_dnd_active = false; } catch {}
  }, [widgetConfig, sections]);
  // ensure global dnd flag cleared if this handler ever runs
  try { (window as Window & { __dashboard_dnd_active?: boolean }).__dashboard_dnd_active = false; } catch {}

  const handleDndDragStart = useCallback((event: DragStartEvent) => {
    try {
      try { (window as Window & { __dashboard_dnd_active?: boolean }).__dashboard_dnd_active = true; } catch {}
      const activeId = String(event.active.id);
      const el = document.getElementById(activeId);
      if (el) {
        const r = el.getBoundingClientRect();
        setDragOverlayState({ instanceId: activeId, width: Math.round(r.width), height: Math.round(r.height) });
      } else {
        setDragOverlayState(null);
      }
    } catch {
      setDragOverlayState(null);
    }
  }, []);

  // Live-reorder during drag so widgets visibly move as you cross them.
  const handleDndDragOver = useCallback((event: DragOverEvent) => {
    const { active, over } = event;
    if (!over) return;
    const activeId = String(active.id);
    const overId = String(over.id);
    if (activeId === overId) return;

    const now = Date.now();
    const last = lastDndReorderRef.current;
    if (last && last.activeId === activeId && last.overId === overId && now - last.at < 120) {
      return;
    }

    const activeParsed = parseInstanceId(activeId);
    const overParsed = parseInstanceId(overId);
    if (!activeParsed) return;
    const activeWidgetId = activeParsed.widgetId;
    const activeSectionId = activeParsed.sectionId;

    let destSectionId: string | null = null;
    let overWidgetId: string | null = null;
    if (overId.startsWith('section-')) {
      destSectionId = overId.slice('section-'.length);
    } else if (overParsed) {
      destSectionId = overParsed.sectionId;
      overWidgetId = overParsed.widgetId;
    }
    if (!destSectionId || destSectionId !== activeSectionId) return;
    if (!overWidgetId) return;

    setSections(prev => {
      const sectionIdx = prev.findIndex(s => String(s.id) === destSectionId);
      if (sectionIdx === -1) return prev;
      const section = prev[sectionIdx];
      const oldIndex = section.widgetIds.indexOf(activeWidgetId);
      const newIndex = section.widgetIds.indexOf(overWidgetId!);
      if (oldIndex === -1 || newIndex === -1 || oldIndex === newIndex) return prev;

      const next = prev.map((s, i) => (i === sectionIdx ? { ...s, widgetIds: [...s.widgetIds] } : s));
      next[sectionIdx].widgetIds.splice(oldIndex, 1);
      next[sectionIdx].widgetIds.splice(newIndex, 0, activeWidgetId);
      lastDndReorderRef.current = { activeId, overId, at: now };
      return next;
    });
  }, []);

  // Back-compat for any legacy per-section dnd-kit render paths that still reference it.
  const handleDndWidgetDragEnd = useCallback((_sectionId: string | number, event: DragEndEvent) => {
    handleDndDragEnd(event);
  }, [handleDndDragEnd]);

  const buildLayoutSnapshot = useCallback((): DashboardLayoutSnapshot => {
    const enabledSections = pruneSectionsForRuntime(sections);
    const widgetLocations = enabledSections.flatMap((section, sectionIndex) =>
      section.widgetIds.map((widgetId, index) => ({
        id: widgetId,
        sectionId: section.id,
        sectionIndex,
        index,
      }))
    );

    const sectionColumns = enabledSections.reduce((acc, section) => {
      const key = String(section.id);
      const savedColumns = sectionColumnsById[key];
      if (Array.isArray(savedColumns) && savedColumns.length) {
        acc[key] = savedColumns.map((column) => filterEnabledWidgetIds([...column]));
        return acc;
      }

      const visibleIds = filterEnabledWidgetIds(section.widgetIds.filter((wid) => widgetConfig[wid]?.visible));
      acc[key] = buildNativeSectionColumns(visibleIds, widgetConfig).map((column) => column.map((card) => card.id));
      return acc;
    }, {} as Record<string, string[][]>);

    return {
      cols,
      order: filterEnabledWidgetIds(widgetOrder),
      widgetLocations,
      sectionColumns,
      widgets: filterEnabledWidgetIds(Object.keys(widgetConfig)).map(id => ({
        id,
        visible: widgetConfig[id]?.visible ?? false,
        size: widgetConfig[id]?.size ?? 'regular'
      })),
      favorites: filterEnabledWidgetIds(favoriteWidgets),
      openTradesColumns,
      sections: enabledSections.map((section) => ({
        ...section,
        widgetIds: pruneWidgetIdsForRuntime(section.widgetIds),
      }))
    };
  }, [cols, widgetOrder, widgetConfig, favoriteWidgets, openTradesColumns, sections, sectionColumnsById, buildNativeSectionColumns]);

  const clearBuiltInPresetClientState = useCallback((presetId: string) => {
    if (!presetId || !isBuiltInPresetId(presetId)) return;
    try {
      removePresetItem('dashboard:saved-layout', presetId);
      removePresetItem('dashboardWidgetOrder', presetId);
      removePresetItem('dashboardLayoutCols', presetId);
      removePresetItem('dashboard:widget-favorites', presetId);
      removePresetItem('widget-dropdown:selected', presetId);
      removePresetItem('dashboard:time-range', presetId);
    } catch {}
    delete layoutsByPresetRef.current[presetId];
  }, [removePresetItem]);

  const applyPresetSnapshotLive = useCallback((
    saved: SavedLayoutSnapshot | DashboardLayoutSnapshot | null | undefined,
    targetPresetId: string,
    sectionColumnsOverride?: Record<string, string[][]>,
    runtimeColumnCount?: number
  ) => {
    if (!saved) return false;
    const shouldRestoreRecentTrades = targetPresetId === 'complete' || targetPresetId === 'trades';
    const ensureRecentTradesSection = (sections: DashboardSection[]) => {
      if (!shouldRestoreRecentTrades) return sections;
      if (!sections.length) return sections;
      if (sections.some((section) => section.widgetIds.includes('openTrades'))) return sections;
      return sections.map((section, index) => (
        index === 0
          ? { ...section, widgetIds: ['openTrades', ...section.widgetIds.filter((id) => id !== 'openTrades')] }
          : section
      ));
    };

    const savedCols = typeof saved.cols === 'number' && [2, 3, 4, 5].includes(saved.cols)
      ? (saved.cols as ColCount)
      : null;

    if (savedCols) {
      prevNativeColumnCountRef.current = runtimeColumnCount ?? savedCols;
      setCols(savedCols);
    }

    if (Array.isArray(saved.order) && saved.order.length) {
      setWidgetOrder(normalizeWidgetOrder(saved.order.map(normalizeWidgetId)));
    }

    setFavoriteWidgets(
      Array.isArray(saved.favorites)
        ? filterEnabledWidgetIds(saved.favorites.filter((id: string) => typeof id === 'string'))
        : []
    );

    const savedWidgets = Array.isArray(saved.widgets) ? saved.widgets : null;
    if (savedWidgets) {
      setWidgetConfig(prev => {
        const isCanonicalSnapshot = saved === BUILT_IN_PRESET_LAYOUTS[targetPresetId];
        const next: DashboardWidgetConfig = isCanonicalSnapshot
          ? buildDefaultWidgetConfig()
          : { ...prev };

        const NO_MINI_IDS = new Set([
          'performanceByTimeOfDay',
          'performanceByRelativeVolatility',
          'performanceByVolumeTraded',
          'performanceByEntryAtr',
          'performanceByInTradePriceRange',
          'performanceByDuration',
          'performanceByDay',
          'performanceByPrice',
          'performanceByOpeningGap',
          'performanceByVolume',
          'performanceByMonth',
          'performanceByAtr',
          'performanceByMovement',
        ]);

        savedWidgets.forEach((w) => {
          if (!w || typeof w.id !== 'string') return;
          const normalizedId = normalizeWidgetId(w.id);
          if (!next[normalizedId]) next[normalizedId] = {};
          next[normalizedId].visible = normalizedId === 'weekSummary' ? true : !!w.visible;
          const savedSize = isWidgetSize(w.size) ? w.size : undefined;
          if (savedSize) {
            if (!isCanonicalSnapshot && normalizedId === 'averageTradePnl' && savedSize === 'large') {
              next[normalizedId].size = 'regular';
            } else if (!isCanonicalSnapshot && NO_MINI_IDS.has(normalizedId) && savedSize === 'mini') {
              next[normalizedId].size = 'regular';
            } else {
              next[normalizedId].size = savedSize;
            }
          }
        });

        if (!next.weekSummary) next.weekSummary = { visible: true, size: 'full', title: 'Week Summary' };
        next.weekSummary.visible = true;
        return isCanonicalSnapshot
          ? pruneWidgetConfigForRuntime(ensureHoldTimeConfig(next))
          : ensureHoldTimeConfig(next);
      });
    }

    setOpenTradesColumns(sanitizeRecentTradesColumnVisibility(saved.openTradesColumns));

    const restoredSections = restoreSectionsFromSavedLayout(saved);
    if (restoredSections.length) {
      setSections(pruneSectionsForRuntime(ensureRecentTradesSection(restoredSections)));
    }

    const sectionColumns = sectionColumnsOverride ?? saved.sectionColumns;
    if (sectionColumns && typeof sectionColumns === 'object') {
      const allIds = new Set(Object.keys(WIDGET_REGISTRY));
      const restoredColumns = Object.entries(sectionColumns).reduce((acc, [sectionId, columns]) => {
        if (!Array.isArray(columns)) return acc;
        const normalizedColumns = columns
          .map((column) => Array.isArray(column) ? column.map(normalizeWidgetId).filter((id) => allIds.has(id)) : []);
        acc[String(sectionId)] = normalizedColumns;
        return acc;
      }, {} as Record<string, string[][]>);
      setSectionColumnsById(restoredColumns);
    } else {
      setSectionColumnsById({});
    }

    latestLayoutRef.current = saved as DashboardLayoutSnapshot;
    hasHydratedRef.current = true;
    return true;
  }, []);

  const applyLayoutSnapshot = useCallback((saved: SavedLayoutSnapshot | DashboardLayoutSnapshot | null | undefined) => {
    void applyPresetSnapshotLive(saved, activePresetId);
  }, [activePresetId, applyPresetSnapshotLive]);

  const applyCanonicalPresetSnapshot = useCallback((presetId: string) => {
    const canonical = BUILT_IN_PRESET_LAYOUTS[presetId];
    if (!canonical) return false;
    return applyPresetSnapshotLive(canonical, presetId, getCanonicalRuntimeSectionColumns(canonical), nativeColumnCount);
  }, [applyPresetSnapshotLive, getCanonicalRuntimeSectionColumns, nativeColumnCount]);

  const persistLayoutForPreset = useCallback((presetId: string, layout: DashboardLayoutSnapshot) => {
    const canonical = BUILT_IN_PRESET_LAYOUTS[presetId];
    const isBuiltInPreset = !!canonical;
    // Allow built-in presets to persist user overrides locally (so "Save" survives logout/login).
    // Reset actions still clear these keys via clearBuiltInPresetClientState.
    const shouldStoreOverride = !isBuiltInPreset || !layoutsEqual(layout, canonical);

    try {
      if (shouldStoreOverride) {
        writePresetItem('dashboard:saved-layout', presetId, JSON.stringify(layout));
        writePresetItem('dashboardWidgetOrder', presetId, JSON.stringify(layout.order));
        writePresetItem('dashboardLayoutCols', presetId, JSON.stringify(layout.cols));
        writePresetItem('dashboard:widget-favorites', presetId, JSON.stringify(layout.favorites ?? []));
        writePresetItem('widget-dropdown:selected', presetId, JSON.stringify((layout.widgets || []).filter((w) => !!w?.visible && w.id !== 'weekSummary').map((w) => w.id)));
        layoutsByPresetRef.current[presetId] = layout;
      } else {
        removePresetItem('dashboard:saved-layout', presetId);
        removePresetItem('dashboardWidgetOrder', presetId);
        removePresetItem('dashboardLayoutCols', presetId);
        removePresetItem('dashboard:widget-favorites', presetId);
        removePresetItem('widget-dropdown:selected', presetId);
        delete layoutsByPresetRef.current[presetId];
      }
    } catch {}
  }, [removePresetItem, writePresetItem]);

  // Keep an in-memory cache so switching presets preserves unsaved changes per preset
  const latestLayoutRef = useRef<DashboardLayoutSnapshot | null>(null);
  const layoutsByPresetRef = useRef<Record<string, DashboardLayoutSnapshot | null>>({});
  const activePresetIdRef = useRef<string>(activePresetId);
  const suppressAutoSaveRef = useRef<boolean>(false);
  const previewSnapshotRef = useRef<DashboardLayoutSnapshot | null>(null);
  const previewPresetIdRef = useRef<string | null>(null);
  // Timestamp of a recent explicit persist to avoid auto-save overwriting it
  const recentlyPersistedRef = useRef<number | null>(null);
  const hasHydratedRef = useRef<boolean>(false);
  const [isPreviewingDefault, setIsPreviewingDefault] = useState<boolean>(false);

  const persistTransientLayoutState = useCallback((layout: DashboardLayoutSnapshot) => {
    const presetId = activePresetIdRef.current;
    if (!presetId) return;

    try {
      layoutsByPresetRef.current[presetId] = layout;
      writePresetItem('dashboard:saved-layout', presetId, JSON.stringify(layout));
      try { writeTenantStorageItem(buildTenantStorageKey('dashboard:saved-layout', userScope), JSON.stringify(layout)); } catch {}
      try { writePresetItem('dashboard:widget-favorites', presetId, JSON.stringify(favoriteWidgets)); } catch {}
      try { recentlyPersistedRef.current = Date.now(); } catch {}
    } catch {}
  }, [favoriteWidgets, userScope, writePresetItem]);

  const buildLayoutDebugSummary = useCallback((layout: DashboardLayoutSnapshot | SavedLayoutSnapshot | null | undefined) => {
    if (!layout) return null;

    const sections = Array.isArray(layout.sections)
      ? layout.sections.map((section) => ({
          id: section?.id ?? null,
          title: section?.title ?? 'Group',
          widgetIds: Array.isArray(section?.widgetIds) ? section.widgetIds.map(normalizeWidgetId) : [],
        }))
      : [];

    return {
      cols: typeof layout.cols === 'number' ? layout.cols : null,
      sections,
      widgetIds: Array.from(new Set(sections.flatMap((section) => section.widgetIds))),
    };
  }, []);

  const stopDefaultPreview = useCallback(() => {
    const snapshot = previewSnapshotRef.current;
    if (!snapshot) {
      previewPresetIdRef.current = null;
      suppressAutoSaveRef.current = false;
      setIsPreviewingDefault(false);
      return;
    }

    suppressAutoSaveRef.current = true;
    applyLayoutSnapshot(snapshot);
    previewSnapshotRef.current = null;
    previewPresetIdRef.current = null;
    setIsPreviewingDefault(false);
    window.setTimeout(() => {
      suppressAutoSaveRef.current = false;
    }, 0);
  }, [applyLayoutSnapshot]);

  const startDefaultPreview = useCallback((presetId: string) => {
    const canonical = BUILT_IN_PRESET_LAYOUTS[presetId];
    if (!canonical) return false;

    previewSnapshotRef.current = buildLayoutSnapshot();
    previewPresetIdRef.current = presetId;
    suppressAutoSaveRef.current = true;
    setIsPreviewingDefault(true);
    applyCanonicalPresetSnapshot(presetId);
    return true;
  }, [applyCanonicalPresetSnapshot, buildLayoutSnapshot]);

  const toggleDefaultPreview = useCallback((presetId?: string) => {
    const targetPresetId = presetId || activePresetIdRef.current;
    if (isPreviewingDefault && previewPresetIdRef.current === targetPresetId) {
      stopDefaultPreview();
      return false;
    }
    return startDefaultPreview(targetPresetId);
  }, [isPreviewingDefault, startDefaultPreview, stopDefaultPreview]);

  useEffect(() => {
    activePresetIdRef.current = activePresetId;
  }, [activePresetId]);
  useEffect(() => {
    latestLayoutRef.current = buildLayoutSnapshot();
  }, [buildLayoutSnapshot]);

  useEffect(() => {
    try {
      const canonical = BUILT_IN_PRESET_LAYOUTS[activePresetId] ?? null;
      const currentLayout = latestLayoutRef.current ?? buildLayoutSnapshot();
      const savedLayout = isPreviewingDefault && previewSnapshotRef.current ? previewSnapshotRef.current : currentLayout;
      window.dispatchEvent(new CustomEvent('dashboard:layout-debug-state', {
        detail: {
          presetId: activePresetId,
          isPreviewingDefault,
          previewPresetId: previewPresetIdRef.current,
          canonical: buildLayoutDebugSummary(canonical),
          current: buildLayoutDebugSummary(currentLayout),
          saved: buildLayoutDebugSummary(savedLayout),
        },
      }));
    } catch {}
  }, [activePresetId, buildLayoutDebugSummary, buildLayoutSnapshot, isPreviewingDefault]);

  useEffect(() => {
    if (!hasHydratedRef.current) return;
    const layout = latestLayoutRef.current ?? buildLayoutSnapshot();
    const canonical = BUILT_IN_PRESET_LAYOUTS[activePresetId];
    const isBuiltInPreset = !!canonical;
    const comparableCanonical = canonical
      ? { ...canonical, sectionColumns: getCanonicalRuntimeSectionColumns(canonical) }
      : canonical;
    const isModified = isBuiltInPreset ? !layoutsEqual(layout, comparableCanonical) : false;
    try {
      window.dispatchEvent(new CustomEvent('dashboard:active-preset-layout-status', {
        detail: { presetId: activePresetId, isBuiltInPreset, isModified },
      }));
    } catch {}
  }, [activePresetId, buildLayoutSnapshot, getCanonicalRuntimeSectionColumns, cols, favoriteWidgets, nativeColumnCount, openTradesColumns, sectionColumnsById, sections, widgetConfig, widgetOrder]);

  useEffect(() => {
    // Wait until hydration has completed before automatically saving changes to UI state
    if (!hasHydratedRef.current || suppressAutoSaveRef.current || isPreviewingDefault) return;
    const layout = latestLayoutRef.current;
    if (!layout) return;
    try {
      persistLayoutForPreset(activePresetIdRef.current, layout);
    } catch {
      // ignore persistence failures
    }
  }, [sections, widgetConfig, cols, widgetOrder, favoriteWidgets, openTradesColumns, sectionColumnsById, isPreviewingDefault, persistLayoutForPreset]);

  // Save the full dashboard layout (cols, widget visibility/sizes, order, favorites)
  const handleSaveLayout = useCallback(async () => {
    if (suppressAutoSaveRef.current || isPreviewingDefault) return;
    try {
      const layout = buildLayoutSnapshot();
      // Persist locally for both built-in and custom presets; built-in presets store an override only when modified.
      persistLayoutForPreset(activePresetIdRef.current, layout);
      try {
        writePresetItem('dashboard:time-range', activePresetIdRef.current, String(timeRangeRef.current || 'month'));
      } catch {}
      if (dashboardUserKey) {
        const headers: Record<string, string> = { 'Content-Type': 'application/json' };
        headers['x-user-id'] = dashboardUserKey;

        const response = await fetch(`/api/dashboard/presets/${encodeURIComponent(activePresetIdRef.current)}`, {
          method: 'POST',
          headers,
          body: JSON.stringify(layout),
        });

        if (!response.ok) {
          const text = await response.text().catch(() => '');
          throw new Error(text || `Save failed with status ${response.status}`);
        }
      }
    } catch (err) {
      console.warn('[preset] save failed', err);
    }
  }, [buildLayoutSnapshot, dashboardUserKey, isPreviewingDefault, persistLayoutForPreset, writePresetItem]);

  const handleSaveLayoutRef = useRef(handleSaveLayout);
  const clearBuiltInPresetClientStateRef = useRef(clearBuiltInPresetClientState);
  const applyCanonicalPresetSnapshotRef = useRef(applyCanonicalPresetSnapshot);
  const dashboardUserKeyRef = useRef(dashboardUserKey);

  useEffect(() => {
    handleSaveLayoutRef.current = handleSaveLayout;
  }, [handleSaveLayout]);

  useEffect(() => {
    clearBuiltInPresetClientStateRef.current = clearBuiltInPresetClientState;
  }, [clearBuiltInPresetClientState]);

  useEffect(() => {
    applyCanonicalPresetSnapshotRef.current = applyCanonicalPresetSnapshot;
  }, [applyCanonicalPresetSnapshot]);

  useEffect(() => {
    dashboardUserKeyRef.current = dashboardUserKey;
  }, [dashboardUserKey]);

  // Allow LayoutPresetsMenu (mounted in the top nav) to control dashboard behaviors via events.
  useEffect(() => {
    const onManage = () => {
      try {
        setWidgetsLauncherTargetSectionId(null);
        setWidgetsLauncherOpen(true);
      } catch {}
    };

    const onSave = () => {
      try {
        void handleSaveLayoutRef.current();
      } catch {}
    };

    const onGrid = (e: Event) => {
      try {
        const n = (e as CustomEvent).detail?.cols;
        if (typeof n !== 'number') return;
        if (![2, 3, 4, 5].includes(n)) return;
        setCols(n as ColCount);
      } catch {}
    };

    const onReset = async (e: Event) => {
      try {
        const presetId = String((e as CustomEvent).detail?.presetId || '').trim();
        if (!presetId) return;
        const canonical = BUILT_IN_PRESET_LAYOUTS[presetId];
        if (!canonical) return;

        // Apply the built-in snapshot before waiting on server cleanup so the
        // dashboard visibly resets in the current session without a refresh.
        setActivePresetId(presetId);
        activePresetIdRef.current = presetId;
        clearBuiltInPresetClientStateRef.current(presetId);
        applyCanonicalPresetSnapshotRef.current(presetId);
        // The reset is applied locally before the server cleanup begins. Report
        // that canonical state immediately so the presets menu does not keep
        // showing a stale modified indicator while DELETE is still pending.
        window.dispatchEvent(new CustomEvent('dashboard:active-preset-layout-status', {
          detail: { presetId, isBuiltInPreset: true, isModified: false },
        }));

        const userKey = dashboardUserKeyRef.current;
        if (userKey) {
          const headers: Record<string, string> = { 'Content-Type': 'application/json' };
          headers['x-user-id'] = userKey;
          const response = await fetch(`/api/dashboard/presets/${encodeURIComponent(presetId)}`, {
            method: 'DELETE',
            headers,
          });

          if (!response.ok) {
            const text = await response.text().catch(() => '');
            throw new Error(text || `Reset failed with status ${response.status}`);
          }
        }
      } catch (err) {
        console.warn('[preset] reset failed', err);
      }
    };

    window.addEventListener('dashboard:request-manage-widgets', onManage as EventListener);
    window.addEventListener('dashboard:request-save-layout', onSave as EventListener);
    window.addEventListener('dashboard:request-grid-change', onGrid as EventListener);
    window.addEventListener('dashboard:request-preset-reset', onReset as EventListener);
    return () => {
      window.removeEventListener('dashboard:request-manage-widgets', onManage as EventListener);
      window.removeEventListener('dashboard:request-save-layout', onSave as EventListener);
      window.removeEventListener('dashboard:request-grid-change', onGrid as EventListener);
      window.removeEventListener('dashboard:request-preset-reset', onReset as EventListener);
    };
  }, []);

  const resizeWidget = (widgetId: string, size: 'full' | 'regular' | 'mini' | 'large') => {
    widgetId = normalizeWidgetId(widgetId);
    setWidgetConfig(prev => ({
      ...prev,
      [widgetId]: { ...getWidgetConfigEntry(prev, widgetId), size }
    }));
  };

  const handleDragStart = (e: React.DragEvent<HTMLElement>, widgetId: string) => {
    setDraggedWidgetId(widgetId);
    draggedWidget.current = widgetId;
    setDraggedSize(getActualWidgetSize(widgetId));
    setDragPlaceholder(null);
    e.dataTransfer.effectAllowed = 'move';
    try {
      e.dataTransfer.setData('text/plain', widgetId);
    } catch {
      // ignore if browser disallows
    }
    const card = (e.currentTarget as HTMLElement).closest('.widget-card') as HTMLElement;

    if (card) {
      // Keep a visual dragging state on the card but avoid creating a custom drag image/overlay.
      setTimeout(() => {
        card.classList.add('dragging');
      }, 0);
    }
  };

  const handleDragOver = (e: React.DragEvent<HTMLElement>) => {
    e.preventDefault();
  };

  const handleDrop = (
    e: React.DragEvent<HTMLElement>,
    dropZoneId: string | null,
    targetSectionId?: string | number,
    insertIndex?: number,
  ) => {
    e.preventDefault();
    let wid = draggedWidget.current;
    if (!wid) {
      try {
        wid = e.dataTransfer.getData('text/plain') || null;
      } catch {
        wid = null;
      }
    }
    if (!wid || wid === dropZoneId) return;

    const widgetId = wid;
    const placeholder = dragPlaceholder;
    const hasPlaceholder = !!placeholder;

    const slotParts = dropZoneId && dropZoneId.startsWith('slot__') ? dropZoneId.split('__') : null;
    const slotSectionId = slotParts && slotParts.length === 3 ? (isNaN(Number(slotParts[1])) ? slotParts[1] : Number(slotParts[1])) : undefined;
    const slotIndex = slotParts && slotParts.length === 3 ? parseInt(slotParts[2], 10) : undefined;

    let resolvedSectionId: string | number | undefined = typeof targetSectionId !== 'undefined' ? targetSectionId : slotSectionId;
    let resolvedInsertIndex: number | undefined = typeof insertIndex === 'number' ? insertIndex : slotIndex;

    if (!hasPlaceholder && !slotParts && dropZoneId) {
      const containing = sections.find(s => s.widgetIds.includes(dropZoneId));
      if (containing) {
        resolvedSectionId = containing.id;
        const visibleInSection = containing.widgetIds.filter(id => getWidgetVisible(widgetConfig, id));
        const idx = visibleInSection.indexOf(dropZoneId);
        if (idx !== -1) resolvedInsertIndex = idx;
      }
    }

    // If the caller provides an explicit insertIndex (e.g., composite placeholders),
    // do not override it with the global dragPlaceholder state.
    const shouldUsePlaceholder =
      typeof insertIndex !== 'number' &&
      hasPlaceholder &&
      placeholder &&
      (typeof resolvedSectionId === 'undefined' || placeholder.sectionId === resolvedSectionId);

    if (shouldUsePlaceholder && placeholder) {
      resolvedSectionId = placeholder.sectionId;
      resolvedInsertIndex = placeholder.index;
    }

      // Update global order: insert before target when dragging from right->left, after when dragging left->right.
    setWidgetOrder(prev => {
      const draggedIndex = prev.indexOf(widgetId);
      if (draggedIndex === -1) return prev;
      const without = prev.filter(id => id !== widgetId);
      let insertionIndex = without.length;

      const getSectionVisibleIds = (sectionId?: string | number) => {
        if (typeof sectionId === 'undefined') return [];
        const section = sections.find(s => s.id === sectionId);
        if (!section) return [];
        return section.widgetIds.filter(id => without.includes(id) && getWidgetVisible(widgetConfig, id));
      };

      const placeWithinSection = (sectionId?: string | number, index?: number) => {
        const ids = getSectionVisibleIds(sectionId);
        if (!ids.length) return without.length;
        const clamped = Math.max(0, Math.min(typeof index === 'number' ? index : ids.length, ids.length));
        if (clamped >= ids.length) {
          const lastId = ids[ids.length - 1];
          const anchorIdx = without.indexOf(lastId);
          return anchorIdx === -1 ? without.length : anchorIdx + 1;
        }
        const anchorId = ids[clamped];
        const anchorIdx = without.indexOf(anchorId);
        return anchorIdx === -1 ? without.length : anchorIdx;
      };

      if (shouldUsePlaceholder && typeof resolvedSectionId !== 'undefined') {
        insertionIndex = placeWithinSection(resolvedSectionId, resolvedInsertIndex);
      } else if (slotParts && typeof slotIndex === 'number') {
        insertionIndex = placeWithinSection(slotSectionId ?? targetSectionId, slotIndex);
      } else if (typeof resolvedSectionId !== 'undefined' && typeof resolvedInsertIndex === 'number') {
        insertionIndex = placeWithinSection(resolvedSectionId, resolvedInsertIndex);
      } else if (dropZoneId) {
        const dropIndexPrev = prev.indexOf(dropZoneId);
        if (dropIndexPrev !== -1) {
          const dropIndexWithout = dropIndexPrev - (draggedIndex < dropIndexPrev ? 1 : 0);
          const movingRight = draggedIndex < dropIndexPrev;
          insertionIndex = movingRight ? dropIndexWithout + 1 : dropIndexWithout;
        }
      } else if (typeof targetSectionId !== 'undefined') {
        const section = sections.find(s => s.id === targetSectionId);
        if (section) {
          const ids = section.widgetIds.filter(id => without.includes(id));
          if (ids.length) {
            const lastId = ids[ids.length - 1];
            const anchorIdx = without.indexOf(lastId);
            insertionIndex = anchorIdx === -1 ? without.length : anchorIdx + 1;
          }
        }
      }

      if (insertionIndex < 0 || insertionIndex > without.length) insertionIndex = without.length;
      without.splice(insertionIndex, 0, widgetId);
      return without;
    });

    // Update section-local order to mirror the same "insert before target" behavior.
        setSections(prev => {
            const next = prev.map(section => ({ ...section, widgetIds: [...section.widgetIds] }));
            const sourceIdx = next.findIndex(s => s.widgetIds.includes(widgetId));
            let targetIdx = typeof resolvedSectionId !== 'undefined'
              ? next.findIndex(s => s.id === resolvedSectionId)
              : -1;
            if (targetIdx === -1 && dropZoneId && !slotParts) {
              targetIdx = next.findIndex(s => s.widgetIds.includes(dropZoneId));
            }
      if (targetIdx === -1 && slotParts && typeof slotSectionId !== 'undefined') {
        targetIdx = next.findIndex(s => s.id === slotSectionId);
      }
      if (targetIdx === -1) targetIdx = sourceIdx;
      if (targetIdx === -1) return prev;

      const targetListPrev = next[targetIdx].widgetIds;
      const dropPosPrev = dropZoneId && !slotParts ? targetListPrev.indexOf(dropZoneId) : -1;
      const draggedPosPrev = targetListPrev.indexOf(widgetId);
      const movingRight = draggedPosPrev !== -1 && dropPosPrev !== -1 && draggedPosPrev < dropPosPrev;

      // Ensure widget is fully removed from ALL sections before inserting
      next.forEach(s => {
        s.widgetIds = s.widgetIds.filter(id => id !== widgetId);
      });

      const targetList = [...next[targetIdx].widgetIds];
      let insertPos = targetList.length;

      const getVisibleOrder = (list: string[]) => list.filter((id) => getWidgetVisible(widgetConfig, id));
      const convertVisibleIndexToActual = (list: string[], index?: number) => {
        const visibleList = getVisibleOrder(list);
        if (!visibleList.length) return list.length;
        const clamped = Math.max(0, Math.min(typeof index === 'number' ? index : visibleList.length, visibleList.length));
        if (clamped >= visibleList.length) return list.length;
        const anchorId = visibleList[clamped];
        const anchorIdx = list.indexOf(anchorId);
        return anchorIdx === -1 ? list.length : anchorIdx;
      };

      if (shouldUsePlaceholder && typeof resolvedInsertIndex === 'number') {
        insertPos = convertVisibleIndexToActual(targetList, resolvedInsertIndex);
      } else if (slotParts && typeof slotIndex === 'number') {
        insertPos = convertVisibleIndexToActual(targetList, slotIndex);
      } else if (typeof resolvedInsertIndex === 'number') {
        insertPos = convertVisibleIndexToActual(targetList, resolvedInsertIndex);
      } else if (dropPosPrev !== -1) {
        const dropPosAdjusted = dropPosPrev - (movingRight ? 1 : 0);
        insertPos = movingRight ? dropPosAdjusted + 1 : dropPosAdjusted;
      } else if (typeof resolvedSectionId !== 'undefined') {
        insertPos = targetList.length;
      }

      if (insertPos < 0 || insertPos > targetList.length) insertPos = targetList.length;
      targetList.splice(insertPos, 0, widgetId);
      next[targetIdx].widgetIds = targetList;
      return next;
    });

    setDragPlaceholder(null);
    setDraggedSize(null);
  };

  const handleDragEnd = useCallback(() => {
    document.querySelectorAll('.widget-card.dragging').forEach(el => el.classList.remove('dragging'));
    draggedWidget.current = null;
    setDraggedWidgetId(null);
    setDragOverWidget(null);
    setDragPlaceholder(null);
    setDraggedSize(null);
  }, []);

  useEffect(() => {
    if (!draggedWidgetId) return;
    const handlePointerDown = (event: PointerEvent) => {
      if (event.button !== 0) return;
      const root = dashboardRef.current;
      const target = event.target as HTMLElement | null;
      if (!target) return;
      if (target.closest('.widget-card') || target.closest('[data-placeholder="true"]')) return;
      if (root && root.contains(target)) {
        handleDragEnd();
        return;
      }
      handleDragEnd();
    };
    document.addEventListener('pointerdown', handlePointerDown, true);
    return () => document.removeEventListener('pointerdown', handlePointerDown, true);
  }, [draggedWidgetId, handleDragEnd]);

  const handlePlaceholderDrop = (e: React.DragEvent<HTMLElement>, sectionId: string | number, insertIndex: number) => {
    if (!layoutMode) return;
    e.preventDefault();
    e.stopPropagation();
    handleDrop(e as React.DragEvent<HTMLElement>, null, sectionId, insertIndex);
  };

  const handleSectionDragOverInternal = (e: React.DragEvent<HTMLDivElement>, sectionId: string | number) => {
    if (!layoutMode || !draggedWidgetId || !draggedSize) return;
    const target = e.target as HTMLElement;
    if (target.closest('[data-widget-card="true"]') || target.closest('[data-placeholder="true"]')) return;
    e.preventDefault();
    const section = sections.find(s => s.id === sectionId);
    const visibleIds = section ? section.widgetIds.filter(id => getWidgetVisible(widgetConfig, id)) : [];
    const index = visibleIds.length;
    updatePlaceholder(sectionId, index);
  };

  // Handle Settings toggles inside Open Trades to show/hide columns
  const handleOpenTradesToggle = (key: string, value: boolean) => {
    const map: Record<string, keyof typeof openTradesColumns> = {
      status: 'status',
      openDate: 'openDate',
      symbol: 'symbol',
      volume: 'volume',
      execs: 'execs',
      notes: 'notes',
      pnl: 'pnl',
      shared: 'shared',
      tags: 'tags',
      side: 'side',
      pnlPercentEquities: 'pnlPercentEquities',
      pnlTicksGrossFutures: 'pnlTicksGrossFutures',
      pnlTicksPerContractGrossFutures: 'pnlTicksPerContractGrossFutures',
      commission: 'commission',
      commissionAndFees: 'commissionAndFees',
      initialRisk: 'initialRisk',
      pnlR: 'pnlR',
    };
    const col = map[key];
    if (!col) return; // ignore keys that don't map to an existing column
    setOpenTradesColumns(prev => ({ ...prev, [col]: value }));
  };

  const { weekData, weeklyTotal } = useMemo(() => {
    const days = buildWeekSummaryFromTrades(authoritativeTrades as Parameters<typeof buildWeekSummaryFromTrades>[0]);
    let totalPnl = 0;
    let totalTrades = 0;

    days.forEach((day) => {
      totalPnl += day.pnl;
      totalTrades += day.trades;
    });

    return { weekData: days, weeklyTotal: { pnl: totalPnl, trades: totalTrades } };
  }, [authoritativeTrades]);

  const weekCards = useMemo((): {
    key: string;
    type: 'day' | 'total';
    label: string;
    subtitle: string;
    pnl: number;
    trades?: number;
    fullDate?: Date;
  }[] => {
    const cards: {
      key: string;
      type: 'day' | 'total';
      label: string;
      subtitle: string;
      pnl: number;
      trades?: number;
      fullDate?: Date;
    }[] = weekData.map((day, idx) => {
      const subtitle = day.fullDate
        ? day.fullDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: displayTimeZone })
        : `Day ${idx + 1}`;
      return {
        key: `day-${idx}`,
        type: 'day' as const,
        label: day.day,
        subtitle,
        pnl: day.pnl,
        trades: day.trades,
        fullDate: day.fullDate,
      };
    });
    cards.push({
      key: 'total',
      type: 'total' as const,
      label: 'Total',
      subtitle: 'WEEK',
      pnl: weeklyTotal.pnl,
      trades: weeklyTotal.trades,
    });
    return cards;
  }, [weekData, weeklyTotal]);

  const totalWinningPnl = useMemo(() => dailyData
    .filter(d => d.pnl > 0)
    .reduce((acc, d) => acc + d.pnl, 0), [dailyData]);

  const totalLosingPnl = useMemo(() => dailyData
    .filter(d => d.pnl < 0)
    .reduce((acc, d) => acc + d.pnl, 0), [dailyData]);

  const isDarkTheme = theme === 'dark';
  const weekSummaryTheme = useMemo(() => {
    if (isDarkTheme) {
      return {
        // remove extra glow so the background stays solid `bg-zinc-900`
        glowClass: '',
        containerClass: 'relative rounded-lg border border-border bg-zinc-900/30 p-6 shadow-[0_10px_30px_rgba(100,116,139,0.06)]',
        titleClass: 'text-white/60',
        labelClass: 'text-white',
        subtitleClass: 'text-white/35',
        tradesTextClass: 'text-white/55',
        amountClasses: {
          inactive: 'text-white/45',
          positive: 'text-emerald-600',
          negative: 'text-red-600'
        },
        // Add a very subtle white glow layer beneath the dark cards to lift them slightly
        dayCardClassNegative: 'bg-gradient-to-b from-[#0d0d10] via-[#141417] to-[#2b0f12] border border-[#20202a] text-white hover:-translate-y-1 hover:border-white/12 shadow-[0_10px_30px_rgba(255,255,255,0.03),0_1px_3px_rgba(0,0,0,0.12),0_6px_18px_rgba(0,0,0,0.25)]',
        dayCardClassPositive: 'bg-gradient-to-b from-[#0d0d10] via-[#141417] to-[#022b17] border border-[#20202a] text-white hover:-translate-y-1 hover:border-white/12 shadow-[0_10px_30px_rgba(255,255,255,0.03),0_1px_3px_rgba(0,0,0,0.12),0_6px_18px_rgba(0,0,0,0.25)]',
        dayCardClassNeutral: 'bg-gradient-to-b from-[#0d0d10] via-[#141417] to-[#18181b] border border-[#20202a] text-white/70 hover:-translate-y-0 hover:border-white/6 shadow-[0_6px_18px_rgba(0,0,0,0.18)]',
        totalCardClassNegative: 'bg-gradient-to-b from-[#242235]/20 via-[#25192a]/50 to-[#3a1012] border border-red-800/70 text-white shadow-[0_8px_24px_rgba(100,116,139,0.06),0_6px_18px_rgba(0,0,0,0.22)]',
        totalCardClassPositive: 'bg-gradient-to-b from-[#022b17]/5 via-[#022b17]/20 to-[#022b17]/100 border border-emerald-800/70 text-white shadow-[0_8px_24px_rgba(16,185,129,0.06),0_6px_18px_rgba(0,0,0,0.22)]',
        totalCardClassNeutral: 'bg-gradient-to-b from-[#1a1a1f] via-[#1a1a1f] to-[#1a1a1f] border border-zinc-800/90 text-white/70 shadow-[0_6px_18px_rgba(0,0,0,0.18)]',
        todayHighlightClassNegative: 'border-2 border-red-800/70 shadow-[0_8px_24px_rgba(220,38,38,0.10)]',
        todayHighlightClassPositive: 'border-2 border-emerald-800/70 shadow-[0_8px_24px_rgba(16,185,129,0.10)]',
        todayHighlightClassNeutral: 'border-2 border-zinc-800/90 shadow-[0_6px_18px_rgba(0,0,0,0.12)]',
        focusRingOffset: 'focus-visible:ring-offset-[#050505]'
      } as const;
    }
    return {
      glowClass: '',
      containerClass: 'relative rounded-lg border border-border bg-gradient-to-b from-slate-100 via-slate-100 to-slate-50 p-6 shadow-[0_10px_30px_rgba(100,116,139,0.04)]',
      titleClass: 'text-slate-500',
      labelClass: 'text-slate-900',
      subtitleClass: 'text-slate-400',
      tradesTextClass: 'text-slate-500',
      amountClasses: {
        inactive: 'text-slate-400',
        positive: 'text-emerald-600',
        negative: 'text-red-600'
      },
      dayCardClassNegative: 'bg-gradient-to-b from-[#fff5f5] via-[#fff1f0] to-[#fff2f2] border border-red-200 text-slate-900 hover:-translate-y-1 hover:border-red-300 shadow-[0_1px_3px_rgba(0,0,0,0.1),0_1px_2px_rgba(0,0,0,0.06),0_6px_18px_rgba(0,0,0,0.06)]',
      dayCardClassPositive: 'bg-gradient-to-b from-[#ecfff3]/40 via-[#ddf7e8]/40 to-[#d1f6de]/50 border border-emerald-200 text-slate-900 hover:-translate-y-1 hover:border-emerald-300 shadow-[0_1px_3px_rgba(0,0,0,0.1),0_1px_2px_rgba(0,0,0,0.06),0_6px_18px_rgba(0,0,0,0.06)]',
      dayCardClassNeutral: 'bg-gradient-to-b from-white via-[#fafafa] to-[#f0f2f5] border border-[#e6e9ef] text-slate-500 hover:-translate-y-0 hover:border-[#eef3ff] shadow-[0_4px_12px_rgba(0,0,0,0.04)]',
      totalCardClassNegative: 'bg-gradient-to-b from-red-500/3 via-red-500/5 to-red-500/30 border border-red-700/80 text-slate-900 shadow-[0_8px_24px_rgba(100,116,139,0.04),0_6px_18px_rgba(0,0,0,0.06)]',
      totalCardClassPositive: 'bg-gradient-to-b from-[#ecfff3]/10 via-[#ddf7e8]/30 to-[#d1f6de] border border-emerald-700/80 text-slate-900 shadow-[0_8px_24px_rgba(16,185,129,0.04),0_6px_18px_rgba(0,0,0,0.04)]',
      totalCardClassNeutral: 'bg-gradient-to-b from-[#f5f6f8] via-[#f5f6f8] to-[#f5f6f8] border border-zinc-800/80 text-slate-500 shadow-[0_4px_12px_rgba(0,0,0,0.04)]',
      todayHighlightClassNegative: 'border-2 border-red-700/80 shadow-[0_6px_18px_rgba(220,38,38,0.06)]',
      todayHighlightClassPositive: 'border-2 border-emerald-700/80 shadow-[0_6px_18px_rgba(16,185,129,0.06)]',
      todayHighlightClassNeutral: 'border-2 border-zinc-800/80 shadow-[0_6px_18px_rgba(16,185,129,0.06)]',
      focusRingOffset: 'focus-visible:ring-offset-white'
    } as const;
  }, [isDarkTheme]);

  // Read favorites from localStorage and sort visible widgets so favorites are always first
  useEffect(() => {
    if (isBuiltInPresetId(activePresetId)) {
      const canonical = BUILT_IN_PRESET_LAYOUTS[activePresetId];
      setFavoriteWidgets(Array.isArray(canonical?.favorites) ? filterEnabledWidgetIds([...canonical.favorites]) : []);
      return;
    }
    try {
      const raw = localStorage.getItem(presetFavoritesKey);
      if (raw) setFavoriteWidgets(filterEnabledWidgetIds(JSON.parse(raw)));
      else setFavoriteWidgets([]);
    } catch {}
  }, [presetFavoritesKey, activePresetId]);

  // React to preset changes from LayoutPresetsMenu
  useEffect(() => {
    const onPresetChange = (e: Event) => {
      try {
        const nextId = (e as CustomEvent).detail?.presetId;
        if (!nextId || typeof nextId !== 'string') return;

        // Cache the current preset's in-memory layout before switching
        try {
          const currentId = activePresetIdRef.current;
          if (currentId && latestLayoutRef.current) {
            const cachedLayout = previewPresetIdRef.current === currentId && previewSnapshotRef.current
              ? previewSnapshotRef.current
              : latestLayoutRef.current;
            layoutsByPresetRef.current[currentId] = cachedLayout;
          }
        } catch {}

        if (previewPresetIdRef.current === activePresetIdRef.current && previewSnapshotRef.current) {
          suppressAutoSaveRef.current = true;
          applyLayoutSnapshot(previewSnapshotRef.current);
          previewSnapshotRef.current = null;
          previewPresetIdRef.current = null;
          setIsPreviewingDefault(false);
          window.setTimeout(() => {
            suppressAutoSaveRef.current = false;
          }, 0);
        }

        setActivePresetId(nextId);
      } catch {}
    };

    window.addEventListener('dashboard:active-preset-changed', onPresetChange as EventListener);
    return () => window.removeEventListener('dashboard:active-preset-changed', onPresetChange as EventListener);
  }, [applyLayoutSnapshot]);

  useEffect(() => {
    const onPreviewDefault = (e: Event) => {
      try {
        const detail = (e as CustomEvent).detail || {};
        const nextPresetId = String(detail.presetId || activePresetIdRef.current || '').trim();
        if (!nextPresetId) return;
        toggleDefaultPreview(nextPresetId);
      } catch {}
    };

    window.addEventListener('dashboard:request-preview-default', onPreviewDefault as EventListener);
    return () => window.removeEventListener('dashboard:request-preview-default', onPreviewDefault as EventListener);
  }, [toggleDefaultPreview]);

  // Hydrate/reset layout whenever the active preset changes.
  useEffect(() => {
    const applyLayout = (saved: SavedLayoutSnapshot | DashboardPresetLayout | DashboardLayoutSnapshot) => {
      if (!saved) return;

      if (isBuiltInPresetId(activePresetId) && shouldMigrateLegacyBuiltInPresetLayout(activePresetId, saved)) {
        applyCanonicalPreset(activePresetId);
        return;
      }

      const savedCols = typeof saved.cols === 'number' && [2, 3, 4, 5].includes(saved.cols)
        ? (saved.cols as ColCount)
        : null;

      if (savedCols) {
        prevNativeColumnCountRef.current = savedCols;
      }

      // Restore cols if present and valid
      if (savedCols) {
        setCols(savedCols);
      }

      // Restore order
      if (Array.isArray(saved.order) && saved.order.length) {
        setWidgetOrder(normalizeWidgetOrder(Array.isArray(saved.order) ? saved.order.map(normalizeWidgetId) : []));
      }

      // Restore favorites
      if (Array.isArray(saved.favorites)) {
        setFavoriteWidgets(filterEnabledWidgetIds(saved.favorites.filter((id: string) => typeof id === 'string')));
      }

      // Restore widget visibility and sizes while ensuring weekSummary remains visible
      const savedWidgets = Array.isArray(saved.widgets) ? saved.widgets : null;
      if (savedWidgets) {
        setWidgetConfig(prev => {
          const next: DashboardWidgetConfig = { ...prev };

          const NO_MINI_IDS = new Set([
            'performanceByTimeOfDay',
            'performanceByRelativeVolatility',
            'performanceByVolumeTraded',
            'performanceByEntryAtr',
            'performanceByInTradePriceRange',
            'performanceByDuration',
            'performanceByDay',
            'performanceByPrice',
            'performanceByOpeningGap',
            'performanceByVolume',
            'performanceByMonth',
            'performanceByAtr',
            'performanceByMovement',
          ]);

          savedWidgets.forEach((w) => {
            if (!w || typeof w.id !== 'string') return;
            const normalizedId = normalizeWidgetId(w.id);
            if (!next[normalizedId]) next[normalizedId] = {};
            next[normalizedId].visible = normalizedId === 'weekSummary' ? true : !!w.visible;
            const savedSize = isWidgetSize(w.size) ? w.size : undefined;
            if (savedSize) {
              // Coerce any legacy/existing 'large' size for averageTradePnl back to 'regular'
              if (normalizedId === 'averageTradePnl' && savedSize === 'large') {
                next[normalizedId].size = 'regular';
              } else if (NO_MINI_IDS.has(normalizedId) && savedSize === 'mini') {
                // Remove mini for the listed widgets by coercing to regular
                next[normalizedId].size = 'regular';
              } else {
                next[normalizedId].size = savedSize;
              }
            }
          });
          // ensure weekSummary present
          if (!next.weekSummary) next.weekSummary = { visible: true, size: 'full', title: 'Week Summary' };
          next.weekSummary.visible = true;
          return pruneWidgetConfigForRuntime(next);
        });
      }

      // Restore open trades columns if present
      if (saved.openTradesColumns && typeof saved.openTradesColumns === 'object' && !Array.isArray(saved.openTradesColumns)) {
        setOpenTradesColumns(sanitizeRecentTradesColumnVisibility(saved.openTradesColumns));
      }

      const restoredSections = restoreSectionsFromSavedLayout(saved);
      if (restoredSections.length) {
        setSections(pruneSectionsForRuntime(restoredSections));
      }

      // Restore native grid placement exactly as it was saved for each section.
      if (saved.sectionColumns && typeof saved.sectionColumns === 'object') {
        const allIds = new Set(Object.keys(WIDGET_REGISTRY));
        const restoredColumns = Object.entries(saved.sectionColumns as Record<string, string[][]>).reduce((acc, [sectionId, columns]) => {
          if (!Array.isArray(columns)) return acc;
          const normalizedColumns = columns
            .map((column) => Array.isArray(column) ? column.map(normalizeWidgetId).filter((id) => allIds.has(id)) : [])
          acc[String(sectionId)] = normalizedColumns;
          return acc;
        }, {} as Record<string, string[][]>);
        setSectionColumnsById(pruneSectionColumnsForRuntime(restoredColumns));
      } else {
        setSectionColumnsById({});
      }

      // Restore the saved timeframe selection (week/month/year) for this preset if present.
      try {
        const raw = readPresetItem('dashboard:time-range', activePresetId);
        if (raw === 'week' || raw === 'month' || raw === 'year') {
          setTimeRange(raw);
        }
      } catch {}
      hasHydratedRef.current = true;
    };

    const applyCanonicalPreset = (presetId: string) => {
      const canonical = BUILT_IN_PRESET_LAYOUTS[presetId];
      if (!canonical) return false;

      prevNativeColumnCountRef.current = nativeColumnCount;
      setCols(canonical.cols as ColCount);
      setWidgetOrder(normalizeWidgetOrder(canonical.order.map(normalizeWidgetId)));
      setWidgetConfig(() => {
        const next = buildDefaultWidgetConfig();
        canonical.widgets.forEach((w) => {
          if (!w || typeof w.id !== 'string') return;
          const id = normalizeWidgetId(w.id);
          if (!next[id]) next[id] = {};
          next[id].visible = id === 'weekSummary' ? true : !!w.visible;
          if (w.size) next[id].size = w.size;
        });
        if (!next.weekSummary) next.weekSummary = { visible: true, size: 'full', title: 'Week Summary' };
        next.weekSummary.visible = true;
        return pruneWidgetConfigForRuntime(next);
      });
      setOpenTradesColumns(sanitizeRecentTradesColumnVisibility(canonical.openTradesColumns));
      setSections(pruneSectionsForRuntime(restoreSectionsFromSavedLayout(canonical)));

      const runtimeSectionColumns = getCanonicalRuntimeSectionColumns(canonical);
      setSectionColumnsById(pruneSectionColumnsForRuntime(runtimeSectionColumns));

      try {
        const raw = readPresetItem('dashboard:time-range', presetId);
        if (raw === 'week' || raw === 'month' || raw === 'year') {
          setTimeRange(raw);
        }
      } catch {}

      latestLayoutRef.current = { ...canonical, sectionColumns: runtimeSectionColumns } as DashboardLayoutSnapshot;
      hasHydratedRef.current = true;
      window.dispatchEvent(new CustomEvent('dashboard:active-preset-layout-status', {
        detail: { presetId, isBuiltInPreset: true, isModified: false },
      }));
      return true;
    };

    const resetToDefaults = () => {
      const order = buildDefaultWidgetOrder();
      prevNativeColumnCountRef.current = 4;
      setCols(4);
      setWidgetOrder(order);
      setWidgetConfig(buildDefaultWidgetConfig());
      setOpenTradesColumns(buildDefaultOpenTradesColumns());
      setSections(pruneSectionsForRuntime(buildDefaultSections(order)));
      setSectionColumnsById({});
      // favorites are stored separately so they can persist without requiring "Save"
      try {
        const rawFavs = localStorage.getItem(presetFavoritesKey);
        if (rawFavs) setFavoriteWidgets(filterEnabledWidgetIds(JSON.parse(rawFavs)));
        else setFavoriteWidgets([]);
      } catch {
        setFavoriteWidgets([]);
      }
      try {
        const raw = readPresetItem('dashboard:time-range', activePresetId);
        if (raw === 'week' || raw === 'month' || raw === 'year') {
          setTimeRange(raw);
        }
      } catch {}
      hasHydratedRef.current = true;
    };

    let cancelled = false;

    const hydratePreset = async () => {
      const builtInPreset = isBuiltInPresetId(activePresetId);

      const cached = builtInPreset ? null : layoutsByPresetRef.current[activePresetId];
      if (cached) {
        applyLayout(cached);
        return;
      }

      if (dashboardUserKey) {
        try {
          const headers: Record<string, string> = { 'Content-Type': 'application/json' };
          headers['x-user-id'] = dashboardUserKey;
          const response = await fetch(`/api/dashboard/presets/${encodeURIComponent(activePresetId)}`, {
            method: 'GET',
            headers,
          });

          if (cancelled) return;

          if (response.ok) {
            const body = await response.json().catch(() => null);
            if (!cancelled && body && body.ok && body.layout) {
              applyLayout(body.layout);
              return;
            }
          }
        } catch (err) {
          console.warn('[preset] hydrate fetch failed', err);
        }
      }

      try {
        const raw = readPresetItem('dashboard:saved-layout', activePresetId);
        if (raw) {
          const saved = JSON.parse(raw);
          if (saved) {
            applyLayout(saved);
            return;
          }
        }
      } catch {}

      if (applyCanonicalPreset(activePresetId)) {
        return;
      }

      resetToDefaults();
    };

    void hydratePreset();
    return () => {
      cancelled = true;
    };
  }, [activePresetId, presetSavedLayoutKey, presetFavoritesKey, dashboardUserKey, getCanonicalRuntimeSectionColumns, persistLayoutForPreset, readPresetItem]);

  // In development, seed the active preset with the repo-provided default layout.
  // In production, rely on per-preset hydration and explicit "Save".
  useEffect(() => {
    try {
      const isDev = (typeof import.meta !== 'undefined' && import.meta.env?.DEV)
        || (typeof process !== 'undefined' && process.env?.NODE_ENV === 'development');
      if (!isDev) return;
      if (isBuiltInPresetId(activePresetId)) return;
      try {
        const existing = readPresetItem('dashboard:saved-layout', activePresetId);
        if (existing) return;
      } catch {}
      try {
        const builtInDefault = BUILT_IN_PRESET_LAYOUTS[activePresetId];
        if (builtInDefault) {
          persistLayoutForPreset(activePresetId, builtInDefault as DashboardLayoutSnapshot);
        }
      } catch {}
    } catch {
      // ignore
    }
  }, [activePresetId, persistLayoutForPreset, presetSavedLayoutKey, readPresetItem]);

  // Sync selections from the standalone WidgetDropdown (if used elsewhere)
  useEffect(() => {
    // On mount, read any existing selection saved by WidgetDropdown
    if (isBuiltInPresetId(activePresetId)) return;
    try {
      const raw = localStorage.getItem(presetWidgetDropdownKey) || localStorage.getItem('widget-dropdown:selected');
      if (raw) {
        const sel: string[] = JSON.parse(raw);
        setWidgetConfig(prev => {
          const next: DashboardWidgetConfig = { ...prev };
          Object.keys(next).forEach((k) => {
            if (k === 'weekSummary') return;
            next[k] = { ...getWidgetConfigEntry(next, k), visible: sel.includes(k) };
          });
          return next;
        });
      }
    } catch {}

    const onStorage = (e: StorageEvent) => {
      if (e.key !== presetWidgetDropdownKey && e.key !== 'widget-dropdown:selected') return;
      try {
        const sel: string[] = e.newValue ? JSON.parse(e.newValue) : [];
        // Mirror legacy changes into the active preset key
        try {
          if (e.key === 'widget-dropdown:selected' && !isBuiltInPresetId(activePresetIdRef.current)) {
            localStorage.setItem(presetWidgetDropdownKey, JSON.stringify(sel));
          }
        } catch {}
        setWidgetConfig(prev => {
          const next: DashboardWidgetConfig = { ...prev };
          Object.keys(next).forEach((k) => {
            if (k === 'weekSummary') return;
            next[k] = { ...getWidgetConfigEntry(next, k), visible: sel.includes(k) };
          });
          return next;
        });
      } catch {}
    };

    const onCustom = (e: Event) => {
      try {
        const sel = (e as CustomEvent).detail as string[];
        if (!isBuiltInPresetId(activePresetIdRef.current)) {
          try { localStorage.setItem(presetWidgetDropdownKey, JSON.stringify(sel)); } catch {}
        }
        setWidgetConfig(prev => {
          const next: DashboardWidgetConfig = { ...prev };
          Object.keys(next).forEach((k) => {
            if (k === 'weekSummary') return;
            next[k] = { ...getWidgetConfigEntry(next, k), visible: sel.includes(k) };
          });
          return next;
        });
      } catch {}
    };

    window.addEventListener('storage', onStorage);
    window.addEventListener('widget-dropdown:selected', onCustom as EventListener);
    return () => {
      window.removeEventListener('storage', onStorage);
      window.removeEventListener('widget-dropdown:selected', onCustom as EventListener);
    };
  }, [activePresetId, presetWidgetDropdownKey]);

  // Expose a callback to update favorites if WidgetsLauncher changes them
  const handleFavoritesChange = (favs: string[]) => {
    const nextFavs = filterEnabledWidgetIds(favs);
    setFavoriteWidgets(nextFavs);
    if (!isBuiltInPresetId(activePresetIdRef.current)) {
      try { localStorage.setItem(presetFavoritesKey, JSON.stringify(nextFavs)); } catch {}
    }
    // Ensure favorited widgets are visible
    setWidgetConfig(prev => {
      const next: DashboardWidgetConfig = { ...prev };
      nextFavs.forEach(id => {
        if (!next[id]) next[id] = {};
        next[id].visible = true;
      });
      return next;
    });
  };

  const visibleWidgets = useMemo(() => {
    // Get all visible widgets except weekSummary
    const allVisible = widgetOrder.filter((id: string) => getWidgetVisible(widgetConfig, id) && id !== 'weekSummary');
    // Sort so favorites (in pinned order) come first while keeping the rest in their saved order
    const favSet = new Set(favoriteWidgets);
    const favs = favoriteWidgets.filter((id: string) => allVisible.includes(id));
    const rest = allVisible.filter((id: string) => !favSet.has(id));
    return [...favs, ...rest];
  }, [widgetOrder, widgetConfig, favoriteWidgets]);

  const handleSectionKitStart = useCallback((event: DragStartEvent) => {
    if (event.active.data.current?.type !== 'section') return;
    setActiveSectionId(String(event.active.id));
  }, []);

  const handleSectionKitOver = useCallback((event: DragOverEvent) => {
    void event;
  }, []);

  const handleSectionKitEnd = useCallback((event: DragEndEvent | DragCancelEvent) => {
    if ('active' in event && event.over && event.active.data.current?.type === 'section' && event.over.data.current?.type === 'section') {
      const from = sections.findIndex(section => String(section.id) === String(event.active.id));
      const to = sections.findIndex(section => String(section.id) === String(event.over!.id));
      if (from >= 0 && to >= 0 && from !== to) setSections(current => arrayMove(current, from, to));
    }
    setActiveSectionId(null);
  }, [sections]);

  // Keep sections synchronized with the canonical widget order and visibility
  useEffect(() => {
    if (USE_NATIVE_DASHBOARD_GRID) return;

    setSections(prev => {
      const orderedVisibleIds = visibleWidgets;
      if (!orderedVisibleIds.length) return prev;

      const visibleSet = new Set<string>(orderedVisibleIds);

      // Build favorites section from favoriteWidgets (only include visible widgets)
      const favIds = favoriteWidgets.filter(id => orderedVisibleIds.includes(id));

      const favSet = new Set<string>(favIds);

      const assigned = new Set<string>();
      const nextSections = prev
        // Remove any existing favorites section from prev so we can reinsert it at the top
        .filter(s => s.id !== 'favorites')
        .map(section => {
          // Preserve per-section ordering; just filter down to visible + non-favorites.
          const seen = new Set<string>();
          const ids = section.widgetIds.filter(id => {
            if (!visibleSet.has(id)) return false;
            if (favSet.has(id)) return false;
            if (seen.has(id)) return false;
            seen.add(id);
            return true;
          });
          ids.forEach(id => assigned.add(id));
          return { ...section, widgetIds: ids };
        });

      const unassigned = orderedVisibleIds.filter(id => !assigned.has(id) && !favSet.has(id));

      // Ensure there's at least one regular section to receive unassigned widgets
      const targetSections = nextSections.length ? nextSections : [{ id: Date.now(), title: 'General', isOpen: true, widgetIds: [] }];
      if (unassigned.length) {
        targetSections[0] = { ...targetSections[0], widgetIds: [...targetSections[0].widgetIds, ...unassigned] };
      }

      // Prepend favorites section if needed
      if (favIds.length) {
        const favSection = { id: 'favorites' as const, title: 'FAVORITES', isOpen: true, widgetIds: favIds };
        return [favSection, ...targetSections];
      }

      return targetSections;
    });
  }, [USE_NATIVE_DASHBOARD_GRID, visibleWidgets, favoriteWidgets]);

  const toggleSectionOpen = (id: number | string) => {
    setSections(prev => prev.map(s => s.id === id ? { ...s, isOpen: !s.isOpen } : s));
  };

  const renameSection = (id: number | string, newTitle: string) => {
    setSections(prev => prev.map(s => s.id === id ? { ...s, title: newTitle } : s));
  };

  const deleteSection = (id: number | string) => {
    setSections(prev => {
      const next = prev.filter(s => s.id !== id);
      if (next.length === 0) return next;
      // Merge deleted section's widgets into the first remaining section
      const deleted = prev.find(s => s.id === id);
      if (deleted && deleted.widgetIds.length) {
        next[0] = { ...next[0], widgetIds: [...next[0].widgetIds, ...deleted.widgetIds] };
      }
      return next;
    });
  };

  const insertSection = (insertIndex?: number) => {
    const newSection = { id: Date.now(), title: 'New Group', isOpen: true, widgetIds: [] as string[] };
    setSections(prev => {
      const next = [...prev];
      if (typeof insertIndex === 'number') {
        next.splice(Math.max(0, Math.min(insertIndex, next.length)), 0, newSection);
      } else {
        next.push(newSection);
      }

      // Persist the new layout immediately using the computed sections so
      // quick refreshes/navigation still see the newly created section.
      try {
        const layout = {
          cols,
          order: widgetOrder,
          widgets: Object.keys(widgetConfig).map(id => ({
            id,
            visible: getWidgetVisible(widgetConfig, id),
            size: getWidgetSize(widgetConfig, id) ?? 'regular'
          })),
          favorites: favoriteWidgets,
          openTradesColumns,
          sections: next
        };
        persistTransientLayoutState(layout);
      } catch {}

      return next;
    });
    // After inserting a section, scroll dashboard to bottom so the new section is visible
    setTimeout(() => {
      const root = dashboardRef.current;
      if (root) {
        try {
          root.scrollTo({ top: root.scrollHeight, behavior: 'smooth' });
        } catch {
          root.scrollTop = root.scrollHeight;
        }
        try { window.scrollTo({ top: document.documentElement.scrollHeight, behavior: 'smooth' }); } catch {}
      }
    }, 60);
  };

  const addSection = () => insertSection();

  const addWidgetToSection = useCallback((sectionId: string | number, widgetId: string) => {
    widgetId = normalizeWidgetId(widgetId);
    // Make widget visible and add to section + global order if missing
    setWidgetConfig(prev => ({ ...prev, [widgetId]: { ...getWidgetConfigEntry(prev, widgetId), visible: true } }));

    setSections(prev => {
      const next = removeWidgetFromSections(prev, widgetId, sectionId).map(s => ({ ...s, widgetIds: [...s.widgetIds] }));
      const idx = next.findIndex(s => String(s.id) === String(sectionId));
      if (idx === -1) return prev;
      if (!next[idx].widgetIds.includes(widgetId)) next[idx].widgetIds.push(widgetId);
      return next;
    });

    setWidgetOrder(prev => prev.includes(widgetId) ? prev : [...prev, widgetId]);
  }, [removeWidgetFromSections, setWidgetConfig, setSections, setWidgetOrder]);

  const applyWidgetsToSection = useCallback((sectionId: string | number, widgetIds: string[]) => {
    const section = sections.find(s => String(s.id) === String(sectionId));
    const prevIds = (section?.widgetIds || []).map(normalizeWidgetId).filter((id) => id !== 'weekSummary');
    const nextIds = widgetIds.map(normalizeWidgetId).filter((id) => id !== 'weekSummary');
    const removedIds = prevIds.filter((id) => !nextIds.includes(id));

    // Replace section widget list (supports clearing). Keep global widgetOrder additive.
    setWidgetConfig(prev => {
      const next: DashboardWidgetConfig = { ...prev };
      nextIds.forEach((id) => {
        next[id] = { ...(next[id] || {}), visible: true };
      });
      removedIds.forEach((id) => {
        next[id] = { ...(next[id] || {}), visible: false };
      });
      return next;
    });

    setSections(prev => {
      const next = prev.map(s => ({ ...s, widgetIds: [...s.widgetIds] }));
      const idx = next.findIndex(s => String(s.id) === String(sectionId));
      if (idx === -1) return prev;
      const nextWithoutMoved = next.map((s, sIdx) => (
        sIdx === idx ? s : { ...s, widgetIds: s.widgetIds.filter((id) => !nextIds.includes(id)) }
      ));
      const normalized = nextWithoutMoved.map(s => ({ ...s, widgetIds: [...s.widgetIds] }));
      normalized[idx].widgetIds = [...nextIds];
      return normalized;
    });

    setWidgetOrder(prev => {
      const next = [...prev];
      nextIds.forEach((id) => {
        if (!next.includes(id)) next.push(id);
      });
      return next;
    });
  }, [sections, setWidgetConfig, setSections, setWidgetOrder]);

  const splitSectionAtRow = useCallback((
    sectionId: string | number,
    visibleCount: number,
    options?: { shouldScroll?: boolean }
  ) => {
    let createdId: number | null = null;
    setSections(prev => {
      const idx = prev.findIndex(s => s.id === sectionId);
      if (idx === -1) return prev;
      const section = prev[idx];
      if (!visibleCount || visibleCount < 0) {
        const next = [...prev];
        const newId = Date.now();
        next.splice(idx, 0, { id: newId, title: 'New Group', isOpen: true, widgetIds: [] });
        createdId = newId;
        try {
          const layout = {
            cols,
            order: widgetOrder,
            widgets: Object.keys(widgetConfig).map(id => ({
              id,
              visible: getWidgetVisible(widgetConfig, id),
              size: getWidgetSize(widgetConfig, id) ?? 'regular'
            })),
            favorites: favoriteWidgets,
            openTradesColumns,
            sections: next
          };
          persistTransientLayoutState(layout);
        } catch {}
        return next;
      }
      let seenVisible = 0;
      let splitIdx = section.widgetIds.length;
      for (let i = 0; i < section.widgetIds.length; i++) {
        const wid = section.widgetIds[i];
        if (getWidgetVisible(widgetConfig, wid)) {
          seenVisible += 1;
        }
        if (seenVisible === visibleCount) {
          splitIdx = i + 1;
          break;
        }
      }
      const headIds = section.widgetIds.slice(0, splitIdx);
      const tailIds = section.widgetIds.slice(splitIdx);
      const next = [...prev];
      next[idx] = { ...section, widgetIds: headIds };
      const newId = Date.now();
      createdId = newId;
      next.splice(idx + 1, 0, { id: newId, title: 'New Group', isOpen: true, widgetIds: tailIds });
      try {
        const layout = {
          cols,
          order: widgetOrder,
          widgets: Object.keys(widgetConfig).map(id => ({
            id,
              visible: getWidgetVisible(widgetConfig, id),
              size: getWidgetSize(widgetConfig, id) ?? 'regular'
          })),
          favorites: favoriteWidgets,
          openTradesColumns,
          sections: next
        };
        persistTransientLayoutState(layout);
      } catch {}
      return next;
    });
    // Persist split (new section created) so it survives refresh/login.
    setTimeout(() => {
      try { handleSaveLayout(); } catch {}
    }, 80);
    // For the row "insert section" divider, we do NOT want to auto-scroll.
    if (createdId !== null && options?.shouldScroll !== false) {
      setRecentlyInsertedSectionId(createdId);
    }
  }, [cols, favoriteWidgets, handleSaveLayout, openTradesColumns, persistTransientLayoutState, widgetConfig, widgetOrder]);

  useEffect(() => {
    if (!recentlyInsertedSectionId) return undefined;
    // Scroll the newly created section into view (if present), then clear the recent marker
    const root = dashboardRef.current;
    if (root) {
      // Try to find the new section element by data attribute; fallback to bottom scroll
      setTimeout(() => {
        const el = document.querySelector(`[data-section-id="${String(recentlyInsertedSectionId)}"]`);
        if (el && el instanceof HTMLElement) {
          try { el.scrollIntoView({ behavior: 'smooth', block: 'nearest' }); } catch { root.scrollTop = root.scrollHeight; }
        } else {
          try { root.scrollTo({ top: root.scrollHeight, behavior: 'smooth' }); } catch { root.scrollTop = root.scrollHeight; }
        }
        try { window.scrollTo({ top: document.documentElement.scrollHeight, behavior: 'smooth' }); } catch {}
      }, 40);
    }
    const timer = window.setTimeout(() => setRecentlyInsertedSectionId(null), 1600);
    return () => window.clearTimeout(timer);
  }, [recentlyInsertedSectionId]);

  // Move a widget into a section when dropped in empty space
  const handleSectionDrop = (e: React.DragEvent<HTMLDivElement>, sectionId: string | number, insertIndex?: number) => {
    const slotId = typeof insertIndex === 'number' ? `slot__${sectionId}__${insertIndex}` : null;
    handleDrop(e as React.DragEvent<HTMLElement>, slotId, sectionId, insertIndex);
  };

  const [dragOverWidget, setDragOverWidget] = useState<string | null>(null);

  const moveSection = (id: string | number, dir: -1 | 1) => {
    setSections(prev => {
      const idx = prev.findIndex(s => s.id === id);
      if (idx === -1) return prev;
      const newIdx = idx + dir;
      if (newIdx < 0 || newIdx >= prev.length) return prev;
      const copy = [...prev];
      const [item] = copy.splice(idx, 1);
      copy.splice(newIdx, 0, item);
      return copy;
    });
  };
  const swapRowsInSection = useCallback((sectionId: string | number, widgetId: string, dir: -1 | 1) => {
    // Swap the row containing `widgetId` with the neighboring row in direction `dir` (-1 up, 1 down).
    setSections(prev => {
      const next = prev.map(s => ({ ...s, widgetIds: [...s.widgetIds] }));
      const secIdx = next.findIndex(s => s.id === sectionId);
      if (secIdx === -1) return prev;
      const section = next[secIdx];
      const visibleIds = section.widgetIds.filter(id => getWidgetVisible(widgetConfig, id));
      if (!visibleIds.length) return prev;

      const normalizedCols = cols && [2,3,4,5].includes(cols) ? cols : 4;
      const rows: string[][] = [];
      let colFill = 0;
      let currentRow: string[] = [];

      visibleIds.forEach(id => {
        const spanUnits = getActualWidgetSize(id) === 'full' ? normalizedCols : 1;
        if (colFill === 0 && spanUnits >= normalizedCols) {
          if (currentRow.length) { rows.push(currentRow); currentRow = []; colFill = 0; }
          rows.push([id]);
          currentRow = [];
          colFill = 0;
        } else {
          currentRow.push(id);
          colFill += spanUnits;
          if (colFill >= normalizedCols) { rows.push(currentRow); currentRow = []; colFill = 0; }
        }
      });
        if (currentRow.length) rows.push(currentRow);

        const rowIndex = rows.findIndex(r => r.includes(widgetId));
      if (rowIndex === -1) return prev;
      const targetIndex = rowIndex + dir;
      if (targetIndex < 0 || targetIndex >= rows.length) return prev;

      const newRows = [...rows];
      [newRows[rowIndex], newRows[targetIndex]] = [newRows[targetIndex], newRows[rowIndex]];
      const newVisibleOrder = newRows.flat();

      // Rebuild section.widgetIds preserving non-visible widgets in place
      let visibleCursor = 0;
      const rebuilt = section.widgetIds.map(id => {
        if (getWidgetVisible(widgetConfig, id)) {
          const nextId = newVisibleOrder[visibleCursor++] ?? id;
          return nextId;
        }
        return id;
      });

      next[secIdx] = { ...section, widgetIds: rebuilt };
      return next;
    });

    // Mirror the visible ordering change in the global widgetOrder while preserving invisible widgets
    setWidgetOrder(prev => {
      const visibleOrder = prev.filter(id => getWidgetVisible(widgetConfig, id) && id !== 'weekSummary');
      if (!visibleOrder.length) return prev;
      const normalizedCols = cols && [2,3,4,5].includes(cols) ? cols : 4;
      const rows: string[][] = [];
      let colFill = 0;
      let currentRow: string[] = [];

      visibleOrder.forEach(id => {
        const spanUnits = getActualWidgetSize(id) === 'full' ? normalizedCols : 1;
        if (colFill === 0 && spanUnits >= normalizedCols) {
          if (currentRow.length) { rows.push(currentRow); currentRow = []; colFill = 0; }
          rows.push([id]);
          currentRow = [];
          colFill = 0;
        } else {
          currentRow.push(id);
          colFill += spanUnits;
          if (colFill >= normalizedCols) { rows.push(currentRow); currentRow = []; colFill = 0; }
        }
      });
      if (currentRow.length) rows.push(currentRow);

      const rowIndex = rows.findIndex(r => r.includes(widgetId));
      if (rowIndex === -1) return prev;
      const targetIndex = rowIndex + dir;
      if (targetIndex < 0 || targetIndex >= rows.length) return prev;

      const newRows = [...rows];
      [newRows[rowIndex], newRows[targetIndex]] = [newRows[targetIndex], newRows[rowIndex]];
      const newVisibleOrder = newRows.flat();

      let cursor = 0;
      const rebuilt = prev.map(id => {
        if (getWidgetVisible(widgetConfig, id) && id !== 'weekSummary') {
          const nextId = newVisibleOrder[cursor++] ?? id;
          return nextId;
        }
        return id;
      });
      return rebuilt;
    });
  }, [cols, getActualWidgetSize, widgetConfig]);

  const handleTimeOfDayIntervalChange = useCallback(
    (v: string) => setTimeOfDayInterval(v as TimeOfDayInterval),
    []
  );

  const widgetPropsCache = useRef<Map<string, { sig: string; props: Record<string, unknown> }>>(new Map());
  const getWidgetProps = (wid: string, pageKey: string, actualSize: WidgetSize) => {
    const pageVal = pageState[pageKey] || 0;
    const sig = `${dashboardTradeSourceSignature}|${timeRange}|${timeOfDayInterval}|${pageVal}|${pageState.hourPage || 0}|${pageState.volumePage || 0}|${pageState.monthPage || 0}|${pageState.atrPage || 0}|${theme}|${totalWinningPnl}|${totalLosingPnl}|${actualSize}${wid === 'openTrades' ? `|${JSON.stringify(openTradesColumns)}` : ''}`;
    const existing = widgetPropsCache.current.get(wid);
    if (existing && existing.sig === sig) return existing.props;
    const props: Record<string, unknown> = {
      dailyData: hasIndependentDashboardTimeframe(wid) ? independentTimeframeDailyData : dailyData,
      trades: wid === 'openTrades' ? dashboardPeriodTrades : authoritativeTrades,
      timeRange,
      size: actualSize,
      ...(wid === 'longTermInvestments' ? { placementHeight: 500, useImportedFallback: false } : {}),
      page: pageVal,
      setPage: (value: number | ((p: number) => number)) => setPage(pageKey, value),
      interval: timeOfDayInterval,
      hourPage: pageState.hourPage || 0,
      setHourPage: (value: number | ((p: number) => number)) => setPage('hourPage', value),
      volumePage: pageState.volumePage || 0,
      setVolumePage: (value: number | ((p: number) => number)) => setPage('volumePage', value),
      monthPage: pageState.monthPage || 0,
      setMonthPage: (value: number | ((p: number) => number)) => setPage('monthPage', value),
      atrPage: pageState.atrPage || 0,
      setAtrPage: (value: number | ((p: number) => number)) => setPage('atrPage', value),
      isDark: theme === 'dark',
      totalWinningPnl: totalWinningPnl,
      totalLosingPnl: totalLosingPnl,
      ...(wid === 'openTrades' ? { visibleColumns: openTradesColumns, onLimitBannerChange: setOpenTradesBanner } : {}),
    };
    widgetPropsCache.current.set(wid, { sig, props });
    return props;
  };

  // Correcting syntax error in renderWidget function
  const renderWidget = (
    widgetId: string,
    sectionId?: string | number,
    opts?: {
      fullWidthControls?: {
        canMoveUp: boolean;
        canMoveDown: boolean;
        onMoveUp?: () => void;
        onMoveDown?: () => void;
      };
    }
  ): React.ReactNode => {
    const config = widgetConfig[widgetId];
    if (!config?.visible) return null;

    const widgetDef = WIDGET_REGISTRY[widgetId];
    if (!widgetDef) return null;
    const widgetScope = getDashboardWidgetScope(widgetId);

    const WidgetComponent = widgetDef.component;
    const pageKey = `${widgetId}Page`;

    // Force specific widgets to render full-size regardless of saved config
    const actualSize = getActualWidgetSize(widgetId);

    // debug logging removed for performance

    const props = getWidgetProps(widgetId, pageKey, actualSize);

    const baseHeightClass = USE_NATIVE_DASHBOARD_GRID
      ? 'h-full'
      : (
          actualSize === 'full'
            ? 'h-[500px]'
            : actualSize === 'large'
              ? 'h-[500px]'
              : actualSize === 'regular'
                ? 'h-[350px]'
                : 'h-[220px]'
        );
    const forceFullWidth = widgetId === 'openTrades';
    const fullWidthClass = USE_NATIVE_DASHBOARD_GRID ? '' : (USE_DND_KIT_GRID && (actualSize === 'full' || forceFullWidth) ? 'col-span-full' : '');
    // Allow per-widget overrides for visual treatment (e.g., transparent background)
    const overrideClasses = widgetId === 'winRateSummary' ? 'bg-transparent shadow-none' : '';

    return (
      <WidgetCard
        key={widgetId}
        id={widgetId}
        title={config.title ?? widgetDef.title ?? widgetId}
        className={`${baseHeightClass} ${fullWidthClass} ${draggedWidgetId === widgetId ? 'ring-1 ring-blue-500 z-20' : ''} relative ${overrideClasses}`}
        size={actualSize}
        headerContent={
          <>
            {widgetScope && (
              <span
                title={widgetScope.description}
                className="inline-flex items-center rounded-full border border-zinc-300 px-2 py-0.5 text-[9px] font-medium normal-case tracking-normal text-zinc-600 dark:border-zinc-700 dark:text-zinc-400"
              >
                {widgetScope.label}
              </span>
            )}
            {widgetId === 'performanceByTimeOfDay' && (
              <TimeOfDayDropdown
                value={timeOfDayInterval}
                onChange={handleTimeOfDayIntervalChange}
                className="scale-90 origin-right"
              />
            )}
          </>
        }
        headerCenter={widgetId === 'openTrades' ? openTradesBanner : undefined}
        onRemove={() => toggleWidget(widgetId)}
        onResize={resizeWidget}
        isResizable={layoutMode && widgetId !== 'weekSummary'}
        dashboardTimeRange={timeRange}
        dashboardTimeRangeRevision={timeRangeSelectionRevision}
        onToggle={widgetId === 'openTrades' ? handleOpenTradesToggle : undefined}
        {...(widgetId === 'openTrades' ? { visibleColumns: openTradesColumns } : {})}
        draggableEnabled={!USE_NATIVE_DASHBOARD_GRID && layoutMode}
        onDragStart={USE_NATIVE_DASHBOARD_GRID ? undefined : (e: React.DragEvent<HTMLElement>) => { if (layoutMode) { e.stopPropagation(); handleDragStart(e, widgetId); } }}
        onDragOver={USE_NATIVE_DASHBOARD_GRID ? undefined : (e: React.DragEvent<HTMLElement>) => { if (layoutMode) { e.preventDefault(); e.stopPropagation(); handleDragOver(e); } }}
        onDrop={USE_NATIVE_DASHBOARD_GRID ? undefined : (e: React.DragEvent<HTMLElement>) => { if (layoutMode) { e.preventDefault(); e.stopPropagation(); handleDrop(e, widgetId, sectionId); } }}
        onDragEnd={USE_NATIVE_DASHBOARD_GRID ? undefined : () => { if (layoutMode) handleDragEnd(); }}
        showVerticalReorder={!!opts?.fullWidthControls}
        canMoveUp={opts?.fullWidthControls?.canMoveUp}
        canMoveDown={opts?.fullWidthControls?.canMoveDown}
        onMoveUp={opts?.fullWidthControls?.onMoveUp}
        onMoveDown={opts?.fullWidthControls?.onMoveDown}
      >
        <DashboardWidgetBoundary>
          <WidgetComponent {...props} />
        </DashboardWidgetBoundary>
      </WidgetCard>
    );
  };

    void DndContext;
    void DragOverlay;
    void closestCenter;
    void SortableContext;
    void verticalListSortingStrategy;
    void Plus;
    void Tooltip;
    void stackedHoverRef;
    void largeAdjRegMiniHover;
    void setLargeAdjRegMiniHover;
    void largeAdjRegMiniRafRef;
    void dndSensors;
    void makeInstanceId;
    void SectionDroppable;
    void SortableGridItem;
    void dragOverlayState;
    void handleDndDragStart;
    void handleDndDragOver;
    void handleDndWidgetDragEnd;
    void handlePlaceholderDrop;
    void splitSectionAtRow;
    void dragOverWidget;


  // When nativeColumnCount changes, redistribute widgets in the Native Dashboard layout
  const prevNativeColumnCountRef = useRef(nativeColumnCount);
  useEffect(() => {
    if (!layoutMode) return;
    if (prevNativeColumnCountRef.current === nativeColumnCount) return;

    setSectionColumnsById(prev => {
      const next = { ...prev };
      let changed = false;
      for (const [sectionId, columnsData] of Object.entries(prev)) {
        const allIds = columnsData.flat();
        if (allIds.length === 0) continue;

        // Use the same sparse distribution logic (map ColumnCard[][] -> string[][])
        const newColsRaw = buildNativeSectionColumns(allIds, widgetConfig);
        const newCols = Array.isArray(newColsRaw)
          ? newColsRaw.map((col) => Array.isArray(col) ? col.map((c: { id: string }) => c.id) : [])
          : [];

        if (JSON.stringify(columnsData) !== JSON.stringify(newCols)) {
          next[sectionId] = newCols;
          changed = true;
        }
      }
      return changed ? next : prev;
    });

    prevNativeColumnCountRef.current = nativeColumnCount;
  }, [nativeColumnCount, layoutMode, buildNativeSectionColumns]);

  const applyNativeSectionColumns = useCallback((sectionId: string | number, nextColumns: string[][]) => {
    const nextVisibleOrderRaw = nextColumns.flat();
    const nativeCount = nativeColumnCount;
    const normalized = (() => {
      const copy = nextColumns.map((column) => Array.isArray(column) ? [...column] : []);
      if (copy.length < nativeCount) {
        while (copy.length < nativeCount) copy.push([]);
      } else if (copy.length > nativeCount) {
        copy.splice(nativeCount);
      }
      return copy;
    })();

    setSectionColumnsById((prev) => ({
      ...prev,
      [String(sectionId)]: normalized,
    }));

    setSections((prev) => prev.map((entry) => {
      if (String(entry.id) !== String(sectionId)) return entry;

      const hiddenIds = entry.widgetIds.filter((wid) => !getWidgetVisible(widgetConfig, wid));

      // Filter out widgets that have been moved out of this section concurrently (e.g. by cross-section handleDrop)
      const validNextVisibleOrder = nextVisibleOrderRaw.filter(wid => entry.widgetIds.includes(wid));
      // Include any widgets that were concurrently added to this section and are visible
      const newlyAddedVisible = entry.widgetIds.filter(wid => !nextVisibleOrderRaw.includes(wid) && getWidgetVisible(widgetConfig, wid));

      return {
        ...entry,
        widgetIds: [...validNextVisibleOrder, ...newlyAddedVisible, ...hiddenIds],
      };
    }));

    setWidgetOrder((prev) => {
      // Re-calculate the previous visible set based on the current `prev` state to avoid closure staleness
      const currentSectionIds = nextVisibleOrderRaw.filter(wid => prev.includes(wid));
      if (currentSectionIds.length === 0) return prev;

      const visibleSet = new Set(currentSectionIds);
      let cursor = 0;

      return prev.map((wid) => {
        if (!visibleSet.has(wid)) return wid;
        const replacement = currentSectionIds[cursor];
        cursor += 1;
        return replacement ?? wid;
      });
    });
  }, [nativeColumnCount, setSectionColumnsById, setSections, setWidgetOrder, widgetConfig]);



  // Save layout mode to localStorage whenever it changes
  useEffect(() => {
    writeTenantStorageItem(buildTenantStorageKey('dashboardLayoutMode'), JSON.stringify(layoutMode));
  }, [layoutMode]);

  const handleEnableDemo = useCallback(() => {
    console.log('[DashboardPage] handleEnableDemo called');
    enableDemoMode();
    handleDashboardTimeRangeSelect('month');
    try {
      dashboardRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
    } catch {}
  }, [handleDashboardTimeRangeSelect]);

  const handleDisableDemo = useCallback(() => {
    window.dispatchEvent(new CustomEvent('tradeproof:request-exit-demo'));
  }, []);

  useEffect(() => {
    const handleDemoToggle = (event: Event) => {
      if (!showDemoUi) return;
      const detail = (event as CustomEvent<{ action?: 'enable' | 'disable' }>).detail;

      if (detail?.action === 'enable') {
        handleEnableDemo();
        return;
      }

      if (detail?.action === 'disable') {
        handleDisableDemo();
        return;
      }

      if (demoModeActive) {
        handleDisableDemo();
      } else {
        handleEnableDemo();
      }
    };

    window.addEventListener(DASHBOARD_DEMO_TOGGLE_EVENT, handleDemoToggle as EventListener);
    return () => window.removeEventListener(DASHBOARD_DEMO_TOGGLE_EVENT, handleDemoToggle as EventListener);
  }, [demoModeActive, handleDisableDemo, handleEnableDemo, showDemoUi]);

  const widgetConfigForLauncher = useMemo<Record<string, { visible: boolean; size?: WidgetSize; title?: string }>>(
    () => Object.entries(widgetConfig).reduce((acc, [id, entry]) => {
      acc[id] = { ...entry, visible: entry.visible === true };
      return acc;
    }, {} as Record<string, { visible: boolean; size?: WidgetSize; title?: string }>),
    [widgetConfig]
  );

  if (isBootstrapping) {
    return (
      <div ref={dashboardRef} className={`min-w-0 scroll-stable overflow-y-auto overflow-x-hidden ${sideMenuActive ? 'pt-0' : ''}`}>
        <DashboardBootstrapSkeleton theme={theme} sideMenuActive={sideMenuActive} />
      </div>
    );
  }

  return (
    <DemoTradesOverrideProvider trades={demoModeActive ? demoTrades : null}>
    <div ref={dashboardRef} className={`min-w-0 scroll-stable overflow-y-auto overflow-x-hidden ${sideMenuActive ? 'pt-0' : ''}`}>
      <div className="mt-3 mb-4 grid min-w-0 grid-cols-1 items-center gap-3 pr-0 xl:grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)]">
          <StandardPageHeader
            title="Dashboard"
            subtitle="Overview of trading performance, activity, and key metrics."
            titleAccessory={<TrialStatusBadge billingState={billingState} billingReady={billingReady} demoModeEnabled={false} onOpenSettings={onOpenSettings} />}
            dataTourTarget="dashboard-overview"
          />
        <div className="flex min-w-0 justify-start xl:mt-8 xl:justify-center">
          <div className="flex max-w-full flex-wrap items-center gap-2 rounded-lg border border-zinc-400 bg-white/90 p-2 dark:border-zinc-700 dark:bg-zinc-900">
            <div>
              <GlassClock />
            </div>
            {/* Thin vertical separator between clock and time-range buttons; 40px spacing each side */}
            <div className="mx-3 h-10 w-px rounded-sm bg-zinc-300/40 dark:bg-zinc-700/60 2xl:mx-10" />
            <div className="flex items-center gap-2">
              <button
                onClick={() => handleDashboardTimeRangeSelect('week')}
                className={`min-h-10 px-4 py-2 text-sm font-normal rounded-lg transition-colors shadow-md ${timeRange === 'week' ? 'font-bold bg-blue-500/20 text-blue-700 border border-blue-200 dark:bg-blue-500/30 dark:text-white dark:border-zinc-700' : 'bg-zinc-100 text-zinc-800 hover:bg-zinc-50 border border-zinc-200 dark:bg-zinc-800 dark:text-zinc-300 dark:hover:bg-zinc-700 dark:border-zinc-700'}`}
              >
                Week
              </button>
              <button
                onClick={() => handleDashboardTimeRangeSelect('month')}
                className={`min-h-10 px-4 py-2 text-sm font-normal rounded-lg transition-colors shadow-md ${timeRange === 'month' ? 'font-bold bg-blue-500/20 text-blue-700 border border-blue-200 dark:bg-blue-500/30 dark:text-white dark:border-zinc-700' : 'bg-zinc-100 text-zinc-800 hover:bg-zinc-50 border border-zinc-200 dark:bg-zinc-800 dark:text-zinc-300 dark:hover:bg-zinc-700 dark:border-zinc-700'}`}
              >
                Month
              </button>
            </div>
            <button
              onClick={() => handleDashboardTimeRangeSelect('year')}
              className={`min-h-10 px-4 py-2 text-sm font-normal rounded-lg transition-colors shadow-md ${timeRange === 'year' ? 'font-bold bg-blue-500/20 text-blue-700 border border-blue-200 dark:bg-blue-500/30 dark:text-white dark:border-zinc-700' : 'bg-zinc-100 text-zinc-800 hover:bg-zinc-50 border border-zinc-200 dark:bg-zinc-800 dark:text-zinc-300 dark:hover:bg-zinc-700 dark:border-zinc-700'}`}
            >
              Year
            </button>
          </div>
        </div>
        <div className="relative flex min-w-0 self-stretch justify-start xl:justify-end">
          <div className="pointer-events-none absolute inset-x-0 bottom-6 hidden h-px bg-zinc-600/60 dark:bg-zinc-500/30 xl:block" aria-hidden="true" />
          <div className="relative z-10 flex min-w-0 flex-wrap items-center gap-2 xl:translate-x-2 xl:translate-y-[3px]">
            {showDemoUi && !sideMenuActive && !demoModePermanentlyDisabled && !demoModeActive && (
              <button
                type="button"
                onClick={handleEnableDemo}
                className="inline-flex min-h-10 items-center gap-2 rounded-xl border border-amber-300/70 bg-[#ad5605] px-4 py-2 text-sm font-semibold text-white shadow-[0_8px_20px_rgba(245,158,11,0.22)] transition-all hover:bg-[#ad5605] hover:shadow-[0_10px_24px_rgba(245,158,11,0.28)] dark:border-amber-300/40 dark:bg-[#ad5605] dark:text-white dark:shadow-[0_8px_20px_rgba(245,158,11,0.18)] dark:hover:bg-[#ad5605]"
              >
                <Sparkles className="h-4 w-4" />
                Try Demo
              </button>
            )}
            <WidgetsLauncher
              theme={theme}
              widgetConfig={widgetConfigForLauncher}
              onToggle={toggleWidget}
              onSelectAll={handleSelectAll}
              onClearAll={handleClearAll}
              favorites={favoriteWidgets}
              onFavoritesChange={handleFavoritesChange}
              open={widgetsLauncherOpen}
              onOpenChange={(v) => { setWidgetsLauncherOpen(v); if (!v) setWidgetsLauncherTargetSectionId(null); }}
              hideTrigger={true}
              anchorRef={layoutPresetsButtonRef}
              targetSectionId={widgetsLauncherTargetSectionId}
              targetSectionWidgetIds={
                widgetsLauncherTargetSectionId !== null && typeof widgetsLauncherTargetSectionId !== 'undefined'
                  ? (sections.find(s => String(s.id) === String(widgetsLauncherTargetSectionId))?.widgetIds || [])
                  : undefined
              }
              onApplySectionWidgets={(sectionId, widgetIds) => {
                applyWidgetsToSection(sectionId, widgetIds);
                setWidgetsLauncherOpen(false);
                setWidgetsLauncherTargetSectionId(null);
              }}
              onSelectWidget={(id, target) => {
                if (typeof target !== 'undefined' && target !== null) {
                  addWidgetToSection(target, id);
                } else {
                  toggleWidget(id);
                }
                setWidgetsLauncherOpen(false);
                setWidgetsLauncherTargetSectionId(null);
              }}
            />
          </div>
        </div>
      </div>

      {demoModeActive ? (
        <div className="mb-4 rounded-2xl border border-amber-200 bg-amber-50/90 px-4 py-3 text-amber-950 shadow-sm dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-100">
          <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
            <div className="flex flex-col gap-2 md:flex-row md:items-center">
              <span className="inline-flex items-center rounded-full bg-amber-100 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.18em] text-amber-800 dark:bg-amber-400/15 dark:text-amber-200">
                Demo Mode
              </span>
              <div className="text-sm text-amber-900 dark:text-amber-100">
                Showing {demoTrades.length} sample trades locally for a walkthrough. Your real imports, broker links, and backend data are untouched.
              </div>
            </div>
            <button
              type="button"
              onClick={handleDisableDemo}
              className="inline-flex min-h-10 items-center justify-center rounded-xl border border-amber-300 bg-white px-4 py-2 text-sm font-semibold text-amber-900 transition-colors hover:bg-amber-100 dark:border-amber-500/40 dark:bg-transparent dark:text-amber-100 dark:hover:bg-amber-500/20"
            >
              Exit Demo
            </button>
          </div>
        </div>
      ) : null}

  {(() => {
    // Responsive grid: always 1 col on mobile, N cols on md+; full-width for special widgets
    return (
      <div
        className={`grid gap-4 items-start w-full`}
        style={{ gridTemplateColumns: `1fr` }}
        data-tour-target="dashboard-widgets"
      >
        {/* Always full-width at top */}
        {widgetConfig.weekSummary?.visible && (
          <div className={`col-span-full widget-fade-in overflow-visible`} style={{ animationDelay: '40ms' }}>
            <div className="relative">
              {weekSummaryTheme.glowClass && (
                <div
                  className={weekSummaryTheme.glowClass}
                  aria-hidden="true"
                />
              )}
              <div className={weekSummaryTheme.containerClass}>
              <div className="flex items-center justify-center mb-4">
                <h2 className={`text-sm font-semibold uppercase mb-2 text-center ${isDarkTheme ? 'text-zinc-400' : 'text-zinc-600'}`}>Week Summary</h2>
              </div>
              <div className="grid grid-cols-4 gap-3 xl:grid-cols-8">
                {weekCards.map((card) => {
                  // Detect if this card corresponds to today's date (local timezone)
                  const today = new Date();
                  const isToday = card.type === 'day' && card.fullDate
                    ? (card.fullDate.getFullYear() === today.getFullYear()
                      && card.fullDate.getMonth() === today.getMonth()
                      && card.fullDate.getDate() === today.getDate())
                    : false;
                  const isTotal = card.type === 'total';
                  const isInactive = card.type === 'day' && card.trades === 0;
                  const amountText = isInactive ? '-' : fmtMoney(card.pnl);
                  const amountColor = isInactive
                    ? weekSummaryTheme.amountClasses.inactive
                    : card.pnl >= 0
                      ? weekSummaryTheme.amountClasses.positive
                      : weekSummaryTheme.amountClasses.negative;
                  const tradesLabel = isInactive
                    ? 'No activity'
                    : `${card.trades} ${card.trades === 1 ? 'trade' : 'trades'}`;
                  const baseClasses = `relative flex h-40 flex-col justify-between !rounded-lg overflow-hidden border px-4 py-4 text-left shadow-sm transition-all duration-300 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 hover:shadow-[0_0_0_1px_rgba(100,100,110,0.28),0_4px_12px_rgba(100,100,110,0.16),0_0_10px_rgba(100,100,110,0.12)] dark:hover:shadow-[0_6px_18px_rgba(37,99,235,0.16),0_0_22px_rgba(37,99,235,0.10),0_0_0_1px_rgba(37,99,235,0.14)] xl:h-48 xl:px-5 ${weekSummaryTheme.focusRingOffset}`;
                  let visualClasses = '';
                  if (isTotal) {
                    if (isInactive) {
                      visualClasses = weekSummaryTheme.totalCardClassNeutral;
                    } else if (typeof card.pnl === 'number' && card.pnl >= 0) {
                      visualClasses = weekSummaryTheme.totalCardClassPositive;
                    } else {
                      visualClasses = weekSummaryTheme.totalCardClassNegative;
                    }
                  } else {
                    visualClasses = (card.type === 'day'
                      ? (isInactive
                          ? weekSummaryTheme.dayCardClassNeutral
                          : (typeof card.pnl === 'number' && card.pnl >= 0
                              ? weekSummaryTheme.dayCardClassPositive
                              : weekSummaryTheme.dayCardClassNegative))
                      : weekSummaryTheme.dayCardClassNegative);
                  }
                  const todayClasses = (() => {
                    if (!isToday) return '';
                    if (isInactive) return weekSummaryTheme.todayHighlightClassNeutral;
                    if (typeof card.pnl === 'number' && card.pnl >= 0) return weekSummaryTheme.todayHighlightClassPositive;
                    return weekSummaryTheme.todayHighlightClassNegative;
                  })();
                  const todayMotionClass = getWeekSummaryCardMotionClass(isToday, isTotal);
                  const Tag = card.type === 'day' && onNavigateToJournalEntry ? 'button' : 'div';
                  const handleClick = () => {
                    if (card.type === 'day' && onNavigateToJournalEntry && card.fullDate) {
                      onNavigateToJournalEntry(card.fullDate);
                    }
                  };
                  const pillClass = (() => {
                    if (!isToday || isTotal) return '';
                    if (isInactive) {
                      return isDarkTheme
                        ? 'absolute left-1/2 top-[calc(50%-10px)] transform -translate-x-1/2 -translate-y-1/2 px-3 py-1 rounded-md text-xs font-semibold uppercase tracking-wider bg-zinc-900 border border-zinc-700 text-zinc-300 shadow-sm z-10'
                        : 'absolute left-1/2 top-[calc(50%-10px)] transform -translate-x-1/2 -translate-y-1/2 px-3 py-1 rounded-md text-xs font-semibold uppercase tracking-wider bg-white border border-[#e6e9ef] text-slate-500 shadow-sm z-10';
                    }
                    if (typeof card.pnl === 'number' && card.pnl >= 0) {
                      return isDarkTheme
                        ? 'absolute left-1/2 top-[calc(50%-10px)] transform -translate-x-1/2 -translate-y-1/2 px-3 py-1 rounded-md text-xs font-semibold uppercase tracking-wider bg-emerald-900 border border-emerald-700 text-emerald-200 shadow-sm z-10'
                        : 'absolute left-1/2 top-[calc(50%-10px)] transform -translate-x-1/2 -translate-y-1/2 px-3 py-1 rounded-md text-xs font-semibold uppercase tracking-wider bg-emerald-50 border border-emerald-200 text-emerald-600 shadow-sm z-10';
                    }
                    return isDarkTheme
                      ? 'absolute left-1/2 top-[calc(50%-10px)] transform -translate-x-1/2 -translate-y-1/2 px-3 py-1 rounded-md text-xs font-semibold uppercase tracking-wider bg-red-500/10 border border-red-700 text-red-300 shadow-sm z-10'
                      : 'absolute left-1/2 top-[calc(50%-10px)] transform -translate-x-1/2 -translate-y-1/2 px-3 py-1 rounded-md text-xs font-semibold uppercase tracking-wider bg-red-50 border border-red-200 text-red-700 shadow-sm z-10';
                  })();

                  return (
                    <Tag
                      key={card.key}
                      onClick={handleClick}
                      className={`${baseClasses} ${visualClasses} ${todayClasses} ${todayMotionClass}`}
                    >
                      {isToday && !isTotal && (
                        <div className={pillClass}>
                          TODAY
                        </div>
                      )}
                      <div className="flex items-center justify-between text-sm">
                        <div className={`text-lg font-semibold tracking-tight ${weekSummaryTheme.labelClass}`}>{card.label}</div>
                        <div className={`text-[11px] uppercase tracking-[0.4em] ${weekSummaryTheme.subtitleClass}`}>{card.subtitle}</div>
                      </div>
                      <div className="w-full">
                            <div className="w-full min-h-10 relative">
                              <p dir="ltr" className={`absolute left-1/2 top-[65%] -translate-x-1/2 -translate-y-1/2 text-2xl font-semibold ${amountColor} tabular-nums`}>{amountText}</p>
                            </div>
                            <p className={`text-xs mt-2 ${weekSummaryTheme.tradesTextClass} w-full text-center`}>{tradesLabel}</p>
                          </div>
                    </Tag>
                  );
                })}
              </div>
            </div>
            </div>
          </div>
        )}

        {/* Sectioned layout: render each user-managed section with its widgets */}
        <DndContext
          collisionDetection={closestCenter}
          onDragStart={handleSectionKitStart}
          onDragOver={handleSectionKitOver}
          onDragEnd={handleSectionKitEnd}
          onDragCancel={handleSectionKitEnd}
        >
        <SortableContext items={sections.map(section => String(section.id))} strategy={verticalListSortingStrategy}>
        {sections.map((section, sidx) => (
          <SortableSection key={section.id} id={section.id} isDragging={activeSectionId === String(section.id)}>
            <SectionSeparator
              id={section.id}
              title={section.title}
              isOpen={section.isOpen}
              onToggle={() => toggleSectionOpen(section.id)}
              onRename={(id, t) => renameSection(id, t)}
              onDelete={(id) => deleteSection(id)}
              theme={theme}
              onSectionDrop={handleSectionDrop}
              onSectionDragOver={(e) => handleSectionDragOverInternal(e as React.DragEvent<HTMLDivElement>, section.id)}
              cols={cols}
              onMoveLeft={() => moveSection(section.id, -1)}
              onMoveRight={() => moveSection(section.id, 1)}
              onMoveUp={() => moveSection(section.id, -1)}
              onMoveDown={() => moveSection(section.id, 1)}
              canMoveUp={sidx > 0}
              canMoveDown={sidx < sections.length - 1}
              onAddWidget={addWidgetToSection}
              onOpenWidgetLauncher={(sectionId) => { setWidgetsLauncherTargetSectionId(sectionId); setWidgetsLauncherOpen(true); }}
            >
              {(() => {
                const sectionVisibleIds = section.widgetIds.filter((wid) => getWidgetVisible(widgetConfig, wid));
                if (sectionVisibleIds.length === 0) return null;

                const fullWidthVisibleIds = sectionVisibleIds.filter((wid) => {
                  const size = getActualWidgetSize(wid);
                  return size === 'full' || (isTabletDashboard && size === 'large');
                });
                const gridVisibleIds = sectionVisibleIds.filter((wid) => !fullWidthVisibleIds.includes(wid));
                const savedColumns = sectionColumnsById[String(section.id)];
                let initialColumnsData: { id: string; rowSpan: number }[][] = [];

                if (Array.isArray(savedColumns) && savedColumns.length) {
                  initialColumnsData = fitNativeSectionColumnsToCount(savedColumns.map((column) =>
                    (Array.isArray(column) ? column : [])
                      .filter((id) => gridVisibleIds.includes(id))
                      .map((id) => ({
                        id,
                        rowSpan: getActualWidgetSize(id) === 'mini' ? 3 : getActualWidgetSize(id) === 'regular' ? 6 : 9
                      }))
                  ), nativeColumnCount);

                  const renderedIds = new Set(initialColumnsData.flatMap(col => col.map(c => c.id)));
                  const missingIds = gridVisibleIds.filter(id => !renderedIds.has(id));

                  if (missingIds.length > 0) {
                    missingIds.forEach((id) => {
                      const rowSpan = getActualWidgetSize(id) === 'mini' ? 3 : getActualWidgetSize(id) === 'regular' ? 6 : 9;
                      let minColIndex = 0;
                      let minHeight = Infinity;

                      const validColCount = Math.max(1, nativeColumnCount || initialColumnsData.length);
                      while (initialColumnsData.length < validColCount) initialColumnsData.push([]);

                      for (let i = 0; i < validColCount; i++) {
                        const colHeight = (initialColumnsData[i] || []).reduce((sum, card) => sum + card.rowSpan, 0);
                        if (colHeight < minHeight) {
                          minHeight = colHeight;
                          minColIndex = i;
                        }
                      }

                      if (initialColumnsData[minColIndex]) {
                        initialColumnsData[minColIndex].push({ id, rowSpan });
                      }
                    });
                  }
                } else {
                  initialColumnsData = buildNativeSectionColumns(gridVisibleIds, widgetConfig);
                }

                return (
                  <>
                    {fullWidthVisibleIds.length > 0 && (
                      <div className="mb-4 space-y-4">
                        {fullWidthVisibleIds.map((wid) => {
                          const visibleIndex = sectionVisibleIds.indexOf(wid);
                          const upNeighborId = layoutMode && visibleIndex > 0
                            ? sectionVisibleIds[visibleIndex - 1]
                            : undefined;
                          const downNeighborId = layoutMode && visibleIndex !== -1 && visibleIndex < sectionVisibleIds.length - 1
                            ? sectionVisibleIds[visibleIndex + 1]
                            : undefined;

                          const fullWidthControls = layoutMode
                            ? {
                                canMoveUp: !!upNeighborId,
                                canMoveDown: !!downNeighborId,
                                onMoveUp: () => swapRowsInSection(section.id, wid, -1),
                                onMoveDown: () => swapRowsInSection(section.id, wid, 1),
                              }
                            : undefined;

                          return (
                            <div key={`${section.id}-${wid}`} className="w-full">
                              {renderWidget(wid, section.id, fullWidthControls ? { fullWidthControls } : undefined)}
                            </div>
                          );
                        })}
                      </div>
                    )}

                    {gridVisibleIds.length > 0 && (
                      <NativeDashboardSectionGrid
                        sectionId={section.id}
                        columnCount={nativeColumnCount}
                        initialColumnsData={initialColumnsData}
                        onColumnsChange={(nextColumns) => applyNativeSectionColumns(section.id, nextColumns)}
                        renderCard={(wid) => renderWidget(wid, section.id)}
                      />
                    )}
                  </>
                );
              })()}
            </SectionSeparator>
          </SortableSection>
        ))}
        </SortableContext>
        </DndContext>

        <div className="col-span-full">
          <AddSectionButton onClick={addSection} />
        </div>
      </div>
    );
  })()}
    </div>
    </DemoTradesOverrideProvider>
  );
}
